import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit
} from '@angular/core';
import {AutocompleteOption, Categorization, Cell, CellEvent, Column, ItemClicked, Row} from './models/models';
import { Hotkey, HotkeysService } from '@components/shared/hotkeys';
import {Subscription} from 'rxjs';
import {AutoUnsubscribe} from '../../../decorators/auto-unsubscribe';
import {NgxPopupComponent, NgxPopupService} from '../../shared/ngx-popups/ngx-popups/ngx-popups';
import {ActivatedRoute, Router} from '@angular/router';
import {DocumentFileService} from '../../../services/document-file.service';
import {SpreadingState} from './state/manager';
import {RowSelectionActionType} from './state/actions';
import {AlertService} from '../../../services/alert.service';
import {SavingPopupComponent} from '../../shared/popups/saving/saving-popup.component';
import { HttpErrorResponse } from '@angular/common/http';
import {SharedDataService} from '../../../services/shared-data.service';
import {ReviewQueueItem} from '../../../models/review-queue-item';
import {Company} from '../../../models/company';
import {UserService} from '../../../services/user.service';
import {StatementDataService} from './data/statement-data.service';
import {GoalSeekService} from '../../../services/goal-seek.service';
import {
  ACCEPTABLE_URLS,
  DYNAMIC_ADJUSTMENT_STATEMENT,
  IS_MAC,
  SCENARIO_TYPE_PROFORMA,
  SCENARIO_TYPE_PROJECTION,
  SPREAD_ACTION_ADD_COLUMN,
  SPREAD_ACTION_ADJUSTMENT_CREATED,
  SPREAD_ACTION_CATEGORIZE,
  SPREAD_ACTION_CLEAR_CATEGORIZATION,
  SPREAD_ACTION_COLUMN_METADATA_CHANGED,
  SPREAD_ACTION_CUT_SELECTED,
  SPREAD_ACTION_DELETE_COLUMNS,
  SPREAD_ACTION_DELETE_ROW,
  SPREAD_ACTION_EDIT_A_CELL,
  SPREAD_ACTION_INVERT_ROWS,
  SPREAD_ACTION_PASTE_SELECTED,
  SPREAD_ACTION_ROW_LABEL_CHANGED,
  SPREAD_ACTION_TOGGLE_COLUMN,
  SPREAD_ACTION_TOGGLE_ROW,
  SPREAD_ACTION_UNCATEGORIZE_ROW,
  SUPPORTED_CURRENCIES,
  SUPPORTED_VIEW_SOURCE_FILE_TYPES,
  TAXONOMY_ID_STATEMENT_TYPE_LOOKUP,
  UNKNOWN_ERROR_TOAST_BODY,
  UNKNOWN_ERROR_TOAST_TITLE,
  UNLABELED_ROW_PREFIX,
  AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG,
  LLM_AUTONORM_CATEGORIZATION_FLAG,
  USER_GUIDES,
} from '../../../utils/constants';
import {Box} from './models/box';
import {ConfirmationPopupComponent} from '../../shared/popups/confirmation/confirmation-popup.component';
import {LaunchDarklyService} from '../../../services/launchdarkly.service';
import {Statement} from '../review/human-validation/models/statement';
import {StatementDataCache} from './data/cache';
import {TrackingService} from '../../../services/tracking.service';
import {BorrowerService} from '../../../services/borrower.service';
import {Logger, LoggingService} from '../../../services/logging.service';
import {SpreadingTemplateService} from '../../../services/spreading-template.service';
import {DataFrameService} from '../../../services/dataframes.service';
import {SpreadingHistoryService} from '../../../services/spreading-history.service';
import {UserGuideService} from '@services/user-guide.service';
import {StatementService} from '@services/statement.service';
import {TranslationService} from '@services/translation.service';
import {TranslationViewStateService} from '@services/translation-view-state.service';
import {DigitizationService} from '@services/shared/digitization/digitization.service';
import {Digitization} from './models/digitization';
import {
  PromptForProjectionNameComponent
} from '@components/main/spreading/popups/prompt-for-projection-name/prompt-for-projection-name.component';
import {
  SelectSpreadingTemplateModalComponent
} from './modals/select-spreading-template-modal/select-spreading-template-modal.component';
import {TemplateItem} from "@models/template-item";
import {CommentModalComponent} from "@components/shared/comment-modal/comment-modal.component";
import {CommentDeleteModalComponent} from "@components/shared/comment-delete-modal/comment-delete-modal.component";
import {
  AttestationPopupComponent
} from "@components/main/spreading/modals/attestation-modal/attestation-popup.component";
import {ROUNDING_MAP, RoundingService, ScaleType} from '@services/rounding.service';
import {
  CommentSaveMetadata,
  StatementCommentActions,
  StatementCommentTypes
} from "@services/shared/comment/comment.service";
import {SubmissionRulesService} from "@services/submission-rules.service";
import {
  NavigationRequest,
  SubmissionRulesValidationModalComponent,
  ValidationResult
} from "./modals/submission-rules-validation-modal/submission-rules-validation-modal.component";
import {ValidateSubmissionRuleService} from "@services/validate-submission-rule.service";
import {SubmissionRuleActions} from "@services/template-data.service";

declare var bootstrap: any;

@Component({
    selector: 'app-spreading',
    templateUrl: './spreading.component.html',
    styleUrls: ['./spreading.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [GoalSeekService],
    standalone: false
})
@AutoUnsubscribe('subsArr$')
export class SpreadingComponent implements OnInit, OnDestroy, AfterViewInit {
  subsArr$: Subscription[] = [];
  // AI disclaimer banner gating flag for the Review Mappings step (AUTO-3408).
  readonly llmAutonormFlag = LLM_AUTONORM_CATEGORIZATION_FLAG;
  state: SpreadingState;
  isDASStatement = false;
  dasRefSnapshot: any;
  stdFrameRefSnapshot: any;
  spreadContainsDASStatement = false;
  loading = true;
  reSpreadInProgress = false;
  alreadyCompleted = false;
  viewMode: 'categorize' | 'preview' | 'alerts' = 'categorize';
  forceIntegrationSync = false;
  reviewQueueItem: ReviewQueueItem = null;
  fileId = -1;
  statementId = -1;
  statementUuid = null;
  relatedStatements: Array<Statement>;
  company: Company = null;
  isError = false;
  currentSum = 0.0;
  viewInNativeLanguage = true;
  private translationDict = new Map<string, string>();

  hasTemplate = false;
  availableTemplates = [];
  selectedTemplate = null;
  isTemplateChooserDisabled = true;
  templateName = '';
  companyObject: Company;

  editingRowLabelIndex = -1;
  editingColumnHeaderIndex = -1;
  editingColumnHeader = 'statementDate';

  contextMenuMode: 'row' | 'sourceCell' | 'column' | 'dasCell' | 'statementDate' | 'sourceCellViewComment' | '' = '';
  contextMenuRow: Row = null;
  contextMenuColumn: Column = null;
  contextMenuCell: Cell = null;
  contextMenuObjectIndex = -1;

  documentSidebarIsOpen: boolean = false;

  submissionRulesSidebarIsOpen: boolean = true;

  viewSourcePage: number;
  viewSourceBox: Box;

  existingColumns = [];

  enableAccessToDoc = false;

  madeChanges = false;

  canCategorize = true;
  logger: Logger;

  originalDocumentFileUrl: string;

  contextMenuStyle = {
    'display': 'none',
    'top': '0px',
    'left': '0px',
  };

  dataCache: StatementDataCache;

  enableGoalSeek = false;
  goalSeekLineItem = -1;
  visibleSuggestionsOriginId = null;

  shouldHideTemplate = false;
  scrollTop = 0;
  scrollLeft = 0;

  UNLABELED_ROW_PREFIX = UNLABELED_ROW_PREFIX;

  mergeTableInsertAtLineItemId = -1;

  cellForComment: Cell;
  rowForComment: Row;

  scenarioTypeProjection = SCENARIO_TYPE_PROJECTION;
  showSourceEnabledInContextMenu = false;

  noMoreUndos = true;
  noMoreRedos = true;
  undoButtonLoading = false;
  redoButtonLoading = false;

  supportedCurrencies = SUPPORTED_CURRENCIES;
  currency: string;
  receivedValue: string = ""
  justPressedEscape = false;

  attestationText: string = '';
  userAcceptedAttestation: boolean = false;
  storedSubmissionRulesValidationResults: ValidationResult[] = null;
  
  protected readonly SubmissionRuleActions = SubmissionRuleActions;

  docPaneWidthPercent = 40;
  sourcePaneWidthPercent = 60;
  private isResizingDocSplit = false;
  private lastDocClientX: number | null = null;

  /**
   * Left/width offset for the bottom bar (AI disclaimer banner + tabs), driven by
   * the document sidebar. Single source of truth so the banner and the tab strip
   * stay aligned and cannot drift apart when the sidebar sizing changes.
   */
  get bottomBarOffsetStyle(): Record<string, string> {
    return this.documentSidebarIsOpen
      ? { left: `${this.docPaneWidthPercent}%`, width: `${100 - this.docPaneWidthPercent}%` }
      : { left: '0', width: '100%' };
  }

  templateSpacerWidthPx = 432; 
  private isResizingTemplateSplit = false;
  private lastTemplateClientX: number | null = null;

  hotkeys: Array<Hotkey> = [
    new Hotkey('shift+down', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      // If they were just shift-upping, shift-down should mean deselect the top one, rather than include the bottom one.
      if (this.state.shiftDirection === 'up') {
        this.state.rowSelectionAction(RowSelectionActionType.DESELECT_FIRST);
      } else {
        this.state.rowSelectionAction(RowSelectionActionType.INCLUDE_NEXT);
      }
      this.recalculateSelectedCellSum(false, 'shift down');
      this.cellEditing({
        cellIdx: -1,
        row: null,
      });
      this._crd.detectChanges();
      return true;
    }),

    new Hotkey('shift+up', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      // If they were just shift-downing, shift-up should mean deselect the bottom one, rather than include the top one.
      if (this.state.shiftDirection === 'down') {
        this.state.rowSelectionAction(RowSelectionActionType.DESELECT_LAST);
      } else {
        this.state.rowSelectionAction(RowSelectionActionType.INCLUDE_PREVIOUS);
      }
      this.recalculateSelectedCellSum(false, 'shift up');
      this._crd.detectChanges();
      return true;
    }),

    new Hotkey('left', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      this.state.columnSelectionAction(RowSelectionActionType.SELECT_PREVIOUS);
      this.recalculateSelectedCellSum(false, 'move key left');
      this._crd.detectChanges();
      return true;
    }),

    new Hotkey('right', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      this.state.columnSelectionAction(RowSelectionActionType.SELECT_NEXT);
      this.recalculateSelectedCellSum(false, 'shift right');
      this._crd.detectChanges();
      return true;
    }),

    // Categorize as a component
    new Hotkey('c', (evt: KeyboardEvent, combo: string) => {
      evt.preventDefault();
      this.categorizeSelectedRows(false);
      return true;
    }),

    // Categorize as a validation
    new Hotkey('v', (evt: KeyboardEvent, combo: string) => {
      evt.preventDefault();
      this.categorizeSelectedRows(true);
      return true;
    }),

    // Select next
    new Hotkey('enter', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      this.state.rowSelectionAction(RowSelectionActionType.SELECT_NEXT);
      this.recalculateSelectedCellSum(false, 'select next');
      this._crd.detectChanges();
      return true;
    }),

    // Select previous
    new Hotkey('shift+enter', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      this.state.rowSelectionAction(RowSelectionActionType.SELECT_PREVIOUS);
      this.recalculateSelectedCellSum(false, 'select previous');
      this._crd.detectChanges();
      return true;
    }),

    // Deselect all
    new Hotkey('escape', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      this.state.rowSelectionAction(RowSelectionActionType.DESELECT_ALL);
      this.stopInProgressCategorizations();
      this.recalculateSelectedCellSum(false, 'deselect all');
      this._crd.detectChanges();
      this.cellEditing({
        cellIdx: -1,
        row: null,
      });
      return true;
    }),

    new Hotkey('alt+i', (evt: KeyboardEvent, combo: string) => {
      this.invertSelectedRows();
      return true;
    }),

    // Uncategorize the selected rows (disabled for DAS statements to protect ManualInputItem rows)
    new Hotkey('u', (evt: KeyboardEvent, combo: string) => {
      if (!this.isDASStatement) {
        this.uncategorizeSelectedRows();
      }
      return true;
    }),

    // Add adjustment for selected row
    new Hotkey('alt+a', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      const selectedRowIdx = this.state.getFirstSelectedRowIndex();
      if (selectedRowIdx < 0) {
        return true;
      }
      this.addAdjustmentRow(selectedRowIdx, this.state.sourceRows[selectedRowIdx]);
      return true;
    }),

    // Edit specific cell
    new Hotkey('f2', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      const selectedRowIdx = this.state.getFirstSelectedRowIndex();
      const selectedColumnIdx = this.state.getLastSelectedItemIndex('column');
      if (selectedColumnIdx < 0 || selectedRowIdx < 0 ) {
        return true;
      }
      this.editCell(this.state.sourceRows[selectedRowIdx], selectedColumnIdx);
      this.receivedValue = this.state.sourceRows[selectedRowIdx].cells[selectedColumnIdx].rawText
      return true;
    }),

    // Insert row below if row selected
    // Insert column right if no row selected
    new Hotkey(['ctrl+plus', 'ctrl+='], (evt: KeyboardEvent, combo: string) => {
      const selectedRowIdx = this.state.getLastSelectedItemIndex('row');
      if (selectedRowIdx < 0) {
        const selectedColumnIdx = this.state.getLastSelectedItemIndex('column');
        if (selectedColumnIdx < 0) {
          return true;
        }
        this.addColumnAtIdx(selectedColumnIdx + 1);
        return true;
      }
      this.addRowAtIdx(selectedRowIdx + 1);
      return true;
    }),


    new Hotkey('alt+backspace', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      const selectedRowIdx = this.state.getFirstSelectedRowIndex();
      if (selectedRowIdx < 0) {
        return true;
      }
      this.deleteRowAtIdx(selectedRowIdx);
      return true;
    }),

    new Hotkey('up', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      if (!this.state.areRowsSelected() || this.state.isFirstRowSelected()) {
        // if no rows selected or the last row is selected, select first row
        const lastRowIndex = this.state.getLastNonHeaderRowIndex();
        this.state.rowSelectionAction(RowSelectionActionType.SELECT_SINGLE, lastRowIndex);
      } else {
        const firstSelectedRowIndex = this.state.beginningSelectedRowIndex();
        this.state.rowSelectionAction(RowSelectionActionType.SELECT_PREVIOUS, firstSelectedRowIndex);
      }
      this.recalculateSelectedCellSum(false, 'select previous');
      this._crd.detectChanges();
      return true;
    }),

    new Hotkey('down', (evt: KeyboardEvent, combo: string) => {
      this.stopEditingText();
      if (!this.state.areRowsSelected() || this.state.isLastRowSelected()) {
        // if no rows selected or the last row is selected, select first row
        const firstRowIndex = this.state.getFirstNonHeaderRowIndex();
        this.state.rowSelectionAction(RowSelectionActionType.SELECT_SINGLE, firstRowIndex);
      } else {
        const lastSelectedRowIndex = this.state.endingSelectedRowIndex();
        this.state.rowSelectionAction(RowSelectionActionType.SELECT_NEXT, lastSelectedRowIndex);
      }
      this.recalculateSelectedCellSum(false, 'select next');
      this._crd.detectChanges();
      return true;
    })
  ];

  // Keep track of validation errors that the user has ignored, by the "uniqid" (row, column, off by amount).
  ignoredValidationErrors: Array<string> = [];

  private delay = (function() {
    let timer: any = null;
    return function(callback: any, ms: number) {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(callback, ms);
    };
  })();

  constructor(
    private _hotkeysService: HotkeysService,
    private _crd: ChangeDetectorRef,
    private _popupService: NgxPopupService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _documentFileService: DocumentFileService,
    private _userService: UserService,
    private _alertService: AlertService,
    private _sharedDataService: SharedDataService,
    private _statementDataService: StatementDataService,
    private _launchDarklyService: LaunchDarklyService,
    private _trackingService: TrackingService,
    private _loggingService: LoggingService,
    private _goalSeekSerice: GoalSeekService,
    private _dataFrameService: DataFrameService,
    private _borrowerService: BorrowerService,
    private _spreadingTemplateService: SpreadingTemplateService,
    private _spreadingHistoryService: SpreadingHistoryService,
    private _statementService: StatementService,
    public userGuideService: UserGuideService,
    private digitizationService: DigitizationService,
    private _roundingService: RoundingService,
    private _submissionRulesService: SubmissionRulesService,
    public validationSubmissionRuleService: ValidateSubmissionRuleService,
    private _translationService: TranslationService,
    private _translationViewState: TranslationViewStateService,
  ) {
    this.logger = this._loggingService.rootLogger.newLogger('UserService');
  }


  resizeDocSplitMouseDown(event: MouseEvent): void {
    if (!this.documentSidebarIsOpen) {
      return;
    }
    event.preventDefault();
    this.isResizingDocSplit = true;
    this.lastDocClientX = event.clientX;
    document.body.style.cursor = 'col-resize';
  }

 

  @HostListener('window:mousemove', ['$event'])
  onDocWindowMouseMove(event: MouseEvent): void {    
    const container = document.querySelector('.data-container') as HTMLElement | null;
    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }

    // Resize left document sidebar
    if (this.isResizingDocSplit && this.documentSidebarIsOpen && this.lastDocClientX !== null) {
      const relativeX = event.clientX - rect.left;
      let newDoc = (relativeX / rect.width) * 100;
      const minPercent = 19;
      const maxPercent = 53;

      if (newDoc < minPercent) {
        newDoc = minPercent;
      }
      if (newDoc > maxPercent) {
        newDoc = maxPercent;
      }

      this.docPaneWidthPercent = newDoc;
      this.sourcePaneWidthPercent = 100 - newDoc;
      this._crd.detectChanges();
    }

    
  }

  @HostListener('window:mouseup')
  onDocWindowMouseUp(): void {
    if (this.isResizingDocSplit) {
      this.isResizingDocSplit = false;
      this.lastDocClientX = null;
      document.body.style.cursor = 'default';
    }
    

  }

  ngOnInit() {
    if (this.isBorrowerLockedForRespread()){
      this.showReSpreadInProgressView()
    }
    
    this.addOrRemoveHotkeys();
    const earlyClassificationUIEnabled = this._launchDarklyService.flags[AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG];
    this.userGuideService.add(earlyClassificationUIEnabled ? USER_GUIDES.REVIEW_MAPPINGS_GUIDE : USER_GUIDES.CATEGORIZING_DATA);
    this.userGuideService.add(USER_GUIDES.CORRECTING_DATA);
    this.userGuideService.add(USER_GUIDES.SPREAD_ASSIST);
    this.userGuideService.add(USER_GUIDES.MERGING_DATA);
    this.userGuideService.add(USER_GUIDES.MAKING_ADJUSTMENTS);
    this.userGuideService.add(USER_GUIDES.COMMENTING);
    this.userGuideService.add(USER_GUIDES.TROUBLESHOOTING_SPREAD);
    this.userGuideService.add(USER_GUIDES.SUPPORTED_TAX_RETURNS);
    this.userGuideService.add(USER_GUIDES.REMOVING_DATA);

    // Due to the way angular optimizes, this component will exist as a "singleton"
    // as long as they are clicking between the tabs at the bottom, even though we
    // are reloading data every time. Each load of the screen within the same
    // file needs 90% of the same data (only thing different is the actual statement
    // rows and columns), so to make things snappier we have a cache instance
    // every time you load this screen.
    this.dataCache = new StatementDataCache();
    if (!this._userService.isSpreader()) {
      this._router.navigate(['companies']);
    }
    this._sharedDataService.hideDefaultNavigation();

    this.subsArr$.push(this._route.paramMap.subscribe(params => {
      this.statementUuid = params.get('statement_uuid');
      this.loadData();
    }));

    this.subsArr$.push(this._sharedDataService.appClicks$.subscribe(() => {
      this.hideContextMenu();
    }));

    this._sharedDataService.pageHeaderTitle$.next('Templates');
  }

  _showDocumentFlyoutIfEnabled() {
    const defaultDocViewSetting = this._launchDarklyService.flags['default-show-document-view-in-data-review-and-categorization'];
    if (!defaultDocViewSetting) {
      return;
    }
    try {
      const localStorageKey = `docExplorerClosed-${this.fileId}`;
      const documentViewClosed = localStorage.getItem(localStorageKey);
      if (documentViewClosed === 'true') {
        return;
      }
    } catch (ex) {}

    this.openDocumentExplorer(null);
  }

  isBorrowerLockedForRespread(): boolean {
    const resolverData = this._route.snapshot.data
    return resolverData.companyIsBeingReSpread
  }

  showReSpreadInProgressView(): void {
    this.reSpreadInProgress = true;
    this.loading = false;
  }

  showComment(event, row: Row, cell: Cell): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuMode = 'sourceCellViewComment'
    this._crd.detectChanges();
  }

  handleValue(value: string) {
    this.receivedValue = value
  }

  openCommentModal(row: Row, cell: Cell, editMode: boolean = false) {
    this._popupService.open({
      componentType: CommentModalComponent,
      inputs: {
        cellWithComment: cell,
        rowWithCell: row,
        spreadingState: this.state,
        isStatementDateComment: false,
        statementDate: '',
      },
      outputs: {
        saveComment: () => {
          this.saveStatementWithComment({
            lineItemLabel: row.label,
            commentType: StatementCommentTypes.LINE_ITEM_COMMENT,
            action: editMode ? StatementCommentActions.UPDATE : StatementCommentActions.CREATE
          });
        }
      }
    });
  }

  openCommentDeleteModal(row: Row, cell: Cell) {
    this._popupService.open({
      componentType: CommentDeleteModalComponent,
      cssClass: 'comment-delete-modal',
      inputs: {
        cellWithComment: cell
      },
      outputs: {
        deleteCommentEvent: () => {
          this.saveStatementWithComment({
            lineItemLabel: row.label,
            commentType: StatementCommentTypes.LINE_ITEM_COMMENT,
            action: StatementCommentActions.DELETE
          });
        }
      }
    });
  }

  openStatementDateCommentModal(row: Row, column: Column, editMode: boolean = false) {
    const commentCell = {
      sourceComments: this.hasComment(column) ? column.sourceComments : []
    };
    this._popupService.open({
      componentType: CommentModalComponent,
      inputs: {
        cellWithComment: commentCell,
        rowWithCell: row,
        spreadingState: this.state,
        isStatementDateComment: true,
        statementDate: column.statementDate,
      },
      outputs: {
        saveComment: () => {
          // Only update the column's sourceComments if the user actually saved
          if (commentCell.sourceComments && commentCell.sourceComments[0]?.statementId == this.statementId) {
            column.sourceComments = commentCell.sourceComments;
          }
          this.saveStatementWithComment({
            commentType: StatementCommentTypes.STATEMENT_LEVEL_COMMENT,
            action: editMode ? StatementCommentActions.UPDATE : StatementCommentActions.CREATE
          });
        },
      },
    });
  }

  openStatementDateCommentDeleteModal(column: Column) {
    this._popupService.open({
      componentType: CommentDeleteModalComponent,
      cssClass: "comment-delete-modal",
      inputs: {
        cellWithComment: { sourceComments: column.sourceComments },
      },
      outputs: {
        deleteCommentEvent: () => {
          // Clear the comments from the column
          column.sourceComments = [];
          this.saveStatementWithComment({
            commentType: StatementCommentTypes.STATEMENT_LEVEL_COMMENT,
            action: StatementCommentActions.DELETE
          });
        },
      },
    });
  }

  addOrRemoveHotkeys() {
    this.hotkeys.push(new Hotkey(['ctrl+z', 'command+z'], (evt: KeyboardEvent, combo: string) => {
      this.undoButtonLoading = true;
      this._crd.detectChanges();
      this.undo();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['ctrl+shift+z', 'command+shift+z'], (evt: KeyboardEvent, combo: string) => {
      this.redoButtonLoading = true;
      this._crd.detectChanges();
      this.redo();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['command+x', 'ctrl+x'], (evt: KeyboardEvent, combo: string) => {
      if (!this.viewInNativeLanguage) { return true; }
      this.cutSelected();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['command+c', 'ctrl+c'], (evt: KeyboardEvent, combo: string) => {
      if (!this.viewInNativeLanguage) { return true; }
      this.copySelected();
      return true;
    }));
    this.hotkeys.push(new Hotkey(['command+v', 'ctrl+v'], (evt: KeyboardEvent, combo: string) => {
      if (!this.viewInNativeLanguage) { return true; }
      this.pasteSelected();
      return true;
    }));
  }

  /**
   * Determines user intent for cut/copy operations based on current selection state.
   * @returns 'row' | 'column' | null - The inferred type, or null if neither is clearly selected
   */
  private inferCutCopyIntent(): 'row' | 'column' | null {
    const selectedRows = this.state.getAllSelectedRows();
    const selectedColumns = this.state.getAllSelectedColumns();
    if (selectedRows.length > 0) {
      return 'row';
    } else if (selectedColumns.length > 0) {
      return 'column';
    }
    return null;
  }

  /**
   * Function that cuts selected row objects or column objects.
   * @param cutType - 'row' to cut rows, 'column' to cut columns. If not provided, infers from selection.
   */
  cutSelected(cutType?: 'row' | 'column' | null): void {
    this.stopEditingText();
    this.clearClipboard();

    // If cutType is not explicitly provided, infer from selection state
    if (!cutType) {
      cutType = this.inferCutCopyIntent();
    }

    // If cutType is explicitly provided from menu button click
    if (cutType === 'column') {
      const colIndices = this.state.getAllSelectedColumnIndices();
      if (colIndices.length > 0) {
        this.madeAChange();
        colIndices.forEach((this.copySelectedColumn), this);
        this.state.deleteSelectedColumns();
        this._alertService.success('Cut column(s)');
      } else {
        this._alertService.info('No columns selected to cut');
      }
    } else if (cutType === 'row') {
      const selectedRows = this.state.getAllSelectedRows();
      if (selectedRows.length > 0) {
        this.madeAChange();
        this.state.clipboard = JSON.parse(JSON.stringify(selectedRows));
        this.state.clipboard.forEach(row => {
          // Exclude uuid from copy otherwise pasted row will have same uuid
          delete row.uuid;
        });
        this.state.deleteSelectedRows();
        this._alertService.success('Cut row(s)');
      } else {
        this._alertService.info('No rows selected to cut');
      }
    } else {
      this._alertService.info('No rows or columns selected to cut');
    }
    this.syncStatementData();
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_CUT_SELECTED);
  }

  /**
   * Function that copies selected row objects or column objects.
   * @param copyType - 'row' to copy rows, 'column' to copy columns. If not provided, infers from selection.
   */
  copySelected(copyType?: 'row' | 'column' | null): void {
    this.stopEditingText();
    this.clearClipboard();

    // If copyType is not explicitly provided, infer from selection state
    if (!copyType) {
      copyType = this.inferCutCopyIntent();
    }

    // If copyType is explicitly provided, use it
    if (copyType === 'column') {
      const colIndices = this.state.getAllSelectedColumnIndices();
      if (colIndices.length > 0) {
        colIndices.forEach((this.copySelectedColumn), this);
        this._alertService.success('Copied column(s)');
      } else {
        this._alertService.info('No columns selected to copy');
      }
    } else if (copyType === 'row') {
      const selectedRows = this.state.getAllSelectedRows();
      if (selectedRows.length > 0) {
        this.state.clipboard = JSON.parse(JSON.stringify(selectedRows));
        this.state.clipboard.forEach(row => {
          // Exclude uuid from copy otherwise pasted row will have same uuid
          delete row.uuid;
        });
        this._alertService.success('Copied row(s)');
      } else {
        this._alertService.info('No rows selected to copy');
      }
    } else {
      this._alertService.info('No rows or columns selected to copy');
    }
  }

   /**
   * Function that pastes clipboard contents to selected column or row.
   */
  pasteSelected(): void {
    this.stopEditingText();
    if (!this.state.clipboard || this.state.clipboard.length <= 0) {
      this._alertService.info('No items copied to the clipboard!');
      return;
    }

    if (this.state.clipboard[0].hasOwnProperty('cells')) {
      const selectedRowIdx = this.state.getFirstSelectedItemIndex('row');
      if (selectedRowIdx < 0) {
        this._alertService.info('Select a row before pasting');
        return;
      }
      this.madeAChange();
      this.state.clipboard.forEach((row, idx) => {
        const index = selectedRowIdx + idx;
        this.state.replaceRowAtIndex(row, index);
      });
      this.state.rebuildRowReferences();
    } else if (this.state.clipboard[0].hasOwnProperty('column')) {
      const selectedColumnIdx = this.state.getFirstSelectedItemIndex('column');
      if (selectedColumnIdx < 0) {
        this._alertService.info('Select a column before pasting');
        return;
      }
      this.madeAChange();
      this.state.clipboard.forEach((col, clipboardIndex) => {
        const colIndex = selectedColumnIdx + clipboardIndex;
        this.state.replaceColumnAtIndex(col, colIndex, clipboardIndex, this.state.clipboard);
      });
    }
    this.syncStatementData();
    this.state.calculate();
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_PASTE_SELECTED);
  }

  copySelectedColumn(colIdx, index): void {
    this.state.clipboard.push([]);
    // Deep copy the column to preserve all metadata (statementDate, reportingInterval, scenario, preparationType, etc.)
    this.state.clipboard[index]['column'] = JSON.parse(JSON.stringify(this.state.columns[colIdx]));
    this.state.clipboard[index]['rows'] = [];
    this.state.sourceRows.forEach(row => {
      const cell = row.cells[colIdx];
      if (cell) {
        // Deep copy the cell to preserve all cell properties
        this.state.clipboard[index]['rows'].push(JSON.parse(JSON.stringify(cell)));
      } else {
        console.warn(`Copy cell at index ${colIdx} in row ${row.label} is undefined`);
      }
    });
  }

  clearClipboard(): void {
    this.state.clipboard = [];
  }

  addRowAtIdx(idx: number) {
    this.madeAChange();
    this.state.addRowsAtIndex(idx, 1);
    this._crd.detectChanges();
  }

  deleteRowAtIdx(idx: number) {
    this.stopEditingText();
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: 'Delete row?',
        text: `Are you sure you want to delete this row? ("${this.state.sourceRows[idx].label}")`,
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this.madeAChange();
            this.state.deleteRowAtIndex(idx);
            this.recalculateSelectedCellSum(true, SPREAD_ACTION_DELETE_ROW);
            this._alertService.success('Row deleted');
            this._crd.detectChanges();
          }
        }
      },
    });
  }

  deleteAllSelectedRows() {
    this.stopEditingText();
    const count = this.state.getAllSelectedRows().length;
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: `Delete ${count} rows?`,
        text: `Are you sure you want to delete these ${count} selected rows?`,
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this.madeAChange();
            this.state.deleteSelectedRows();
            this._alertService.success('Rows deleted');
            this._crd.detectChanges();
          }
        }
      },
    });
  }

  madeAChange() {
    this.madeChanges = true;
  }

  /**
   * Synchronizes the working state with the statement data for proper saving.
   */
  private syncStatementData() {
    this.state.statement.rows = this.state.sourceRows;
    this.state.statement.columns = this.state.columns;
  }

  columnHeaderDropDownChanged() {
    this.madeChanges = true;
    this.storeNewState(SPREAD_ACTION_COLUMN_METADATA_CHANGED);
    this.refreshTabStatusAfterEdit();
  }

  addColumnAtIdx(idx: number) {
    this.madeAChange();
    this.stopEditingText();
    this.state.addColumnsAtIndex(idx, 1);
    this.syncStatementData();
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_ADD_COLUMN);
  };

  deleteSelectedColumns() {
    const selectedColumns = this.state.getAllSelectedColumns();
    let question = 'Delete column?';
    let text = 'Are you sure you want to delete this column?';
    let msg = 'Column deleted!';

    if (selectedColumns.length > 1) {
      question = `Delete ${selectedColumns.length} columns?`;
      text = `Are you sure you want to delete these ${selectedColumns.length} columns?`;
      msg = 'Columns deleted!';
    }
    this.stopEditingText();
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: question,
        text: text,
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this.madeAChange();
            this.state.deleteSelectedColumns();
            this.recalculateSelectedCellSum(true, SPREAD_ACTION_DELETE_COLUMNS);
            this._alertService.success(msg);
            this.syncStatementData();
            this._crd.detectChanges();
          }
        }
      },
    });
  }

  invertSelectedRows() {
    this.madeAChange();
    this.stopEditingText();
    this.state.invertSelectedRows();
    this.recalculateSelectedCellSum(true, SPREAD_ACTION_INVERT_ROWS);
    this._crd.detectChanges();
  }

  showSourceForCell(evt, row: Row, cellIdx: number) {
    this.stopEditingText();
    const ableToShowSourceBoxOrPage = this.updateSourceCellBox(row, cellIdx);
    if (ableToShowSourceBoxOrPage) {
      this.documentSidebarIsOpen = true;
      this.clearDocExplorerClosedTracking();
      this._crd.detectChanges();
    } else {
      this._alertService.warning('Could not identify source cell or source page.', 'Warning', 5);
    }
  }

  showSourceForRow(evt, row: Row) {
    this.stopEditingText();
    
    if (row?.lineItemLabelSourceBox) {
      this.resetDocumentViewSourceBox();
      const pageNumber = row.lineItemLabelSourceBox.pageNumber;
      if (this.sourceBoxIsValid(row.lineItemLabelSourceBox, pageNumber)) {
        this.viewSourceBox = row.lineItemLabelSourceBox;
        this.viewSourcePage = row.lineItemLabelSourceBox.pageNumber;
        this.documentSidebarIsOpen = true;
        this.clearDocExplorerClosedTracking();
        this._crd.detectChanges();
        return;
      } else if (pageNumber > -1) {
        this.viewSourceBox = null;
        this.viewSourcePage = pageNumber;
        this.documentSidebarIsOpen = true;
        this.clearDocExplorerClosedTracking();
        this._crd.detectChanges();
        return;
      }
    }

    this._alertService.warning('Could not identify source cell or source page.', 'Warning', 5);
  }

  updateSourceCellBox(row: Row, cellIdx: number) {
    this.resetDocumentViewSourceBox();
    const pageNumber = row.cells[cellIdx].pageNumber;
    if (row.cells[cellIdx]?.sourceBox && row.cells[cellIdx].sourceBox !== null && this.sourceBoxIsValid(row.cells[cellIdx].sourceBox, pageNumber)) {
      this.viewSourceBox = row.cells[cellIdx].sourceBox;
      this.viewSourcePage = this.viewSourceBox.pageNumber;

      this._crd.detectChanges();
      return true;
    } else if (pageNumber > -1) {
      this.viewSourceBox = null;
      this.viewSourcePage = row.cells[cellIdx].pageNumber;
      this._alertService.warning('The source cell could not be identified but the source page has been found.', 'Warning', 5);
      this._crd.detectChanges();
      return true;
    }
    return false;
  }

  sourceBoxIsValid(sourceBox, pageNumber) {
    const hasCoord = (c: { x?: number; y?: number } | null | undefined) =>
      c != null && typeof c.x === 'number' && typeof c.y === 'number';
    const pageOk =
      pageNumber == null ||
      pageNumber === -1 ||
      pageNumber === sourceBox?.pageNumber ||
      (pageNumber === 0 && sourceBox?.pageNumber != null);
    return (
      sourceBox?.llc != null &&
      sourceBox?.lrc != null &&
      sourceBox?.ulc != null &&
      sourceBox?.urc != null &&
      hasCoord(sourceBox.llc) &&
      hasCoord(sourceBox.lrc) &&
      hasCoord(sourceBox.ulc) &&
      hasCoord(sourceBox.urc) &&
      pageOk
    );
  }

  setShowSourceEnabledInContextMenu() {
    if (!this.isDASStatement && SUPPORTED_VIEW_SOURCE_FILE_TYPES.includes(this.state.documentFile.fileType)) {
      this.showSourceEnabledInContextMenu = true;
    }
  }

  openMergeTablesInterface() {
    const endingSelectedRow = this.state.endingSelectedRow();
    if (endingSelectedRow && (endingSelectedRow.lineItemId || endingSelectedRow.uuid)) {
      this.mergeTableInsertAtLineItemId = endingSelectedRow.lineItemId;
      this.saveThenNavigateToDedicatedDocumentManagerView('../merge-table', endingSelectedRow.lineItemId, endingSelectedRow.uuid)
    }
  }

  openManageFootnotesInterface() {
    const endingSelectedRow = this.state.endingSelectedRow();
    if (endingSelectedRow && (endingSelectedRow.lineItemId || endingSelectedRow.uuid)) {
      this.saveThenNavigateToDedicatedDocumentManagerView('../manage-footnotes', endingSelectedRow.lineItemId, endingSelectedRow.uuid)
    }
  }

  saveThenNavigateToDedicatedDocumentManagerView(targetPath: string, targetLineItemId: number, targetRowUuid?: string) {
    // so we don't lose data while saving merged table
    this._popupService.open({
      componentType: SavingPopupComponent,
      cssClass: 'modal-confirmation'
    });
    this.saveBeforeNavigatingAway()
      .then(() => {
        this._popupService.closeAll();
        this._alertService.success('Spread saved!');
        this.loading = true;
        this._crd.detectChanges();
        const queryParams: Record<string, any> = { targetLineItem: targetLineItemId };
        if (targetRowUuid) {
          queryParams.targetRowUuid = targetRowUuid;
        }
        this._router.navigate([targetPath], {
          relativeTo: this._route,
          queryParams
        }).finally(() => this.loading = false);
      })
      .catch((err) => {
        this._popupService.closeAll();
        this.loading = false;
        this._crd.detectChanges();
        console.log('Error saving data: ', err);
        this._alertService.error("Unable to save in-progress spread. Make sure the current data is valid and save before proceeding.")
      });
  }

  categorizeSelectedRows(asValidation = false) {
    if (!this.canCategorize) {
      return;
    }
    this.madeAChange();
    this.stopEditingText();
    this.getScrollPosition();
    const firstSelectedRow = this.state.getFirstSelectedRow()
    if (!firstSelectedRow || firstSelectedRow.header || firstSelectedRow.itemClass==='supplementalItem') {
      return;
    }

    firstSelectedRow.showMappingBox = true;
    firstSelectedRow.mappingBoxValidation = asValidation;
    this.assignGoalSeekFlag();
    this._crd.detectChanges();
    this._focusOnAutocomplete();
  }

  getScrollPosition() {
    const containerEl = document.getElementById('src-container');
    if (containerEl) {
      this.scrollTop = containerEl.scrollTop;
      this.scrollLeft = containerEl.scrollLeft;
    }
  }

  setScrollPosition() {
    const containerEl = document.getElementById('src-container');
    if (containerEl) {
      containerEl.scrollTop = this.scrollTop;
      containerEl.scrollLeft = this.scrollLeft;
    }
  }

  toggleDocumentExplorer(evt) {
    if (this.reSpreadInProgress){
      return
    }
    if (this.documentSidebarIsOpen) {
      this.closeDocumentExplorer();
    } else {
      this.openDocumentExplorer(evt);
    }
  }

  toggleSubmissionRulesSidebar(evt) {
    this.submissionRulesSidebarIsOpen = !this.submissionRulesSidebarIsOpen;
    this._crd.detectChanges();
  }


  /**
   * Opens up the document explorer without showing any boxes
   */
  openDocumentExplorer(evt) {
    this.stopEvent(evt);

    // Empty it out
    this.resetDocumentViewSourceBox();
    this.documentSidebarIsOpen = true;
    this.clearDocExplorerClosedTracking();
    this._crd.detectChanges();
  }

  closeDocumentExplorer(skipTracking: boolean = false) {
    this.documentSidebarIsOpen = false;
    if (!skipTracking) {
      this.trackDocumentExplorerClose();
    }
    this._crd.detectChanges();
  }

  /**
   * Track the user action of manually closing the document explorer in local storage so it can remain closed after when
   * navigating between tabs.
   */
  trackDocumentExplorerClose() {
    if (this.fileId < 0) {
      return
    }
    const localStorageKey = `docExplorerClosed-${this.fileId}`;
    localStorage.setItem(localStorageKey, 'true');
  }
  
  clearDocExplorerClosedTracking() {
    const localStorageKey = `docExplorerClosed-${this.fileId}`;
    localStorage.removeItem(localStorageKey);
  }

  uncategorizeSelectedRows() {
    this.madeAChange();
    this.stopEditingText();
    this.state.clearAllCategorizations(this.state.getAllSelectedRows());
    this.assignGoalSeekFlag()
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_UNCATEGORIZE_ROW);
  }

  removeSuggestionFromRow(evt: Event, row: Row) {
    this.madeAChange();
    this.stopEditingText();
    this.state.clearGoalSeekSuggestion(row);
    this._crd.detectChanges();
  }

  resetUI() {
    this.madeChanges = false;
    this.currentSum = 0.0;
    this.closeDocumentExplorer(true);
    this.resetDocumentViewSourceBox();
  }

  private restoreNavState(): void {
    const { shouldHideTemplate: stateHideTemplate, documentSidebarIsOpen: stateSidebarOpen, ...remainingState }
      = (history.state ?? {}) as { shouldHideTemplate?: boolean; documentSidebarIsOpen?: boolean; [key: string]: any };
    if (stateHideTemplate !== undefined) {
      this.shouldHideTemplate = stateHideTemplate;
    }
    if (stateSidebarOpen !== undefined) {
      this.documentSidebarIsOpen = stateSidebarOpen;
    }
    history.replaceState(remainingState, '');
  }

  getDocumentFileId() {
    const s3KeyParts = this.state.documentFile.name.split('/')
    // a change to the s3 key will have the uuid either as the first or second part of the key string
    return this._userService.getBankId() == s3KeyParts[0] ? s3KeyParts[1] : s3KeyParts[0];

  }

  hideSuggestions(row: Row) {
    this.visibleSuggestionsOriginId = null;
  }

  goalSeek(row: Row) {
    row.goalSeekInProgress = true;
    setTimeout(() => {
      this.goalSeekLineItem = row.lineItemId;
      const selectedColumnIdx = this.state.getLastSelectedItemIndex('column');
      const solution = this._goalSeekSerice.solveGoalSeek(this.state.getGoalSeekSourceRows(row, selectedColumnIdx), -1.0 * row.cells[selectedColumnIdx].offBy, selectedColumnIdx);
      if (!solution) {
        this.goalSeekLineItem = -1;
        this._alertService.warning('Our system was unable to find potential solutions. Categorize more items then try again.', 'No Solutions Found');
      } else {
        this.visibleSuggestionsOriginId = this.goalSeekLineItem;
        this.goalSeekLineItem = -1;
        this.state.updateFromGoalSeekSuggestions(solution, this.visibleSuggestionsOriginId);
        this.scrollToVisibleSuggestion();
      }
      row.goalSeekInProgress = false;
      this._crd.detectChanges();
    }, 0)
  }

  scrollToVisibleSuggestion() {
    setTimeout(() => {
      const visibleTooltips = document.querySelectorAll('.goalSeekTooltip');
      if (visibleTooltips.length) {
        const firstVisibleTooltip =   visibleTooltips[0];
        firstVisibleTooltip.scrollIntoView({behavior: "smooth", block: "center"});
      }
    });
  }

  assignGoalSeekFlag() {
    // goal seek for balance sheet is conditional only on the feature flag
    if (this.state.statementType === 'BALANCE_SHEET') {
      this.enableGoalSeek = true;
    } else if (this.state.statementType === 'INCOME_STATEMENT') {
      this.enableGoalSeek = this.state.enableGoalSeekForIncomeStatement();
    } else {
      this.enableGoalSeek = false;
    }
  }

  async loadData(newState: any = null) {
    this.resetUI();
    this.restoreNavState();
    this.loading = true;
    this._crd.detectChanges();
    // Load the statement by ID from the URL
    this._statementDataService.loadStatementByUuid(this.statementUuid).then((statement: Statement) => {
      this.isDASStatement = statement.statementType == DYNAMIC_ADJUSTMENT_STATEMENT;
      this.statementId = statement.id;
      this.fileId = statement.documentFileId;
      return Promise.all([
        this.loadSpreadingState(statement),
        this.loadRelatedStatements(statement),
        this.loadStandardDataFrameRefSnapshot(statement)
      ]);
      }).then(() => {
        this.relatedStatements = this.sortStatements(this.relatedStatements, this.state.taxonomy);
        this.loading = false;
        this.assignGoalSeekFlag();
        this._sharedDataService.pageHeaderTitle$.next(this.state.documentFile.companyName.toString());
        this.hotkeys.forEach(hk => {
          this._hotkeysService.add(hk);
        });

        this.setEnableAccessToDoc();

        if (this.state.rows.length === 0) {
          // No template! Cannot categorize
          this.canCategorize = false;
        } else {
          this.canCategorize = true;
        }

        this._dataFrameService.getPeriodsWithData(this.state.documentFile.company).subscribe(data => {
          const tempArray = [];
          data.forEach(element => {
            tempArray.push({
              preparationType: element.preparationType,
              fiscalYearEnd: element.fiscalYearEnd,
              reportingPeriod: element.value,
              currencyFormat: element.currencyFormat,
            });
          });

          this.existingColumns = tempArray;
        });

        this._crd.detectChanges();

        this._restoreTranslationView();

        this.navigateToSubmissionRuleResult();

        this._showDocumentFlyoutIfEnabled();

        try {
          this._trackingService.trackHumanInLoop({
            type: 'Start',
            step: 'Spreading',
            documentFileId: this.getDocumentFileId(),
            documentCompanyId: this.state.documentFile.company,
            documentTenantId: this._userService.getBankId()
          });
        } catch (err) {
          this.logger.error('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
        }
      }).catch((err) => {
        if (err.error_type === 'ObjectDoesNotExist') {
          this._alertService.error('Review queue item could not be found. It may have been deleted.');
        } else {
          this._alertService.error('Oops! Something went wrong. (' + err.message + ')');
          this.logger.error('Error when loading spreading data: ' + err.message, {'errorObject': err});
        }
    }).catch((err) => {
      this.logger.error('Error when loading spreading data: ' + err.message, {'errorObject': err});
    });
  }

  private loadStandardDataFrameRefSnapshot(statement: Statement): Promise<void> {
    if (this.isDASStatement) {
      const period_key_uuids = statement?.columns.map(col => col?.periodKeyUuid)
      return new Promise<void>((resolve, reject) => {
        this._statementService.loadSTDFrameRefSnapshot(
          statement.companyId, period_key_uuids, statement.documentFileId, statement.spreadingTemplateId).subscribe(
          res => {
            if (this.state) {
              this.state.stdFrameRefSnapshot = res
              this.state.calculate();
              this._crd.detectChanges();
            } else {
              this.stdFrameRefSnapshot = res;
            }
            resolve();
          },
          error => {
            reject(error);
          }
        )
      });
    } else {
      // No need to load std frame ref snapshot for non-DAS statements
      return Promise.resolve();
    }
  }

  swapLanguage() {
    this.viewInNativeLanguage = !this.viewInNativeLanguage;
    this._translationViewState.set(this._documentFileId(), this.viewInNativeLanguage);
    this._crd.detectChanges();
  }

  private _documentFileId(): number | string | null {
    return this.state?.documentFile?.id ?? null;
  }

  private _restoreTranslationView(): void {
    if (!this.hasTranslations()) {
      return;
    }
    this.viewInNativeLanguage = this._translationViewState.get(this._documentFileId());
    this._crd.detectChanges();
  }

  hasTranslations(): boolean {
    return !!(this.state?.statement?.detectedLanguage && this.state.statement.detectedLanguage.language !== 'en');
  }

  rowHasTranslation(row: Row): boolean {
    return !!(row.translatedLabel && row.translatedLabel !== row.label) || !!row.labelEditedByUser;
  }

  loadSpreadingState(statement: Statement) {
    return this._statementDataService.loadState(statement, this.dataCache).then((state: SpreadingState) => {
      this.translationDict.clear();
      [...(state.sourceRows ?? []), ...(state.rows ?? [])].forEach(row => {
        if (row.translatedLabel === 'None' || row.translatedLabel === '') {
          row.translatedLabel = undefined;
        }
        if (row.label && row.translatedLabel && row.translatedLabel !== row.label) {
          this.translationDict.set(row.label.trim(), row.translatedLabel);
        }
      });
      this.state = state;
      if (this.dasRefSnapshot) {
        this.state.dasRefSnapshot = this.dasRefSnapshot;
      }
      if (this.stdFrameRefSnapshot) {
        this.state.stdFrameRefSnapshot = this.stdFrameRefSnapshot;
      }
      this.state.calculate();
      this._crd.detectChanges();
      // only initialize if currently empty.
      if (this._spreadingHistoryService.shouldThisBeInitialized()) {
        this._spreadingHistoryService.initializeNewHistory(state);
      }
      if (state.reviewQueueItem && state.reviewQueueItem.embeddedWorkflow) {
        this._sharedDataService.embeddedWorkflow$.next(state.reviewQueueItem.embeddedWorkflow);
      }

      this.selectedTemplate = this.state.spreadingTemplateId;
      this.loadCompanyInfo(statement);
      this.setShowSourceEnabledInContextMenu();
      this.loadOriginalDocumentDataForDownload();
    });
  }

  loadCompanyInfo(statement: Statement) {
    this._borrowerService.getCompanyById(statement.companyId).subscribe((company: Company) => {
      this.isTemplateChooserDisabled = company.defaultSpreadingTemplate !== null;
      this.companyObject = company;
      // once the templateChooserDisabled flag is set, we can load in the template info
      this.loadTemplateInfo();
    });
  }


  loadTemplateInfo() {
    // TODO potential performance improvement: we do not need to load ALL of the templates data here
    this.subsArr$.push(this._spreadingTemplateService.getTemplates('STANDARD', true).subscribe( (data) => {
      const availableTemps = [];

      data.forEach(template => {
        availableTemps.push({'id': template.id, 'text': template.name, 'items': template.items })
        if (this.state.spreadingTemplateId === template.id) {
          this.templateName = template.name;
          this.attestationText = template?.attestationText;
          this.state.submissionRules = this._submissionRulesService.parseTemplateSubmissionRules(template?.submissionRules);
          this.state.templateHasSubmissionRules = Object.keys(this.state.submissionRules).length > 0;
          if (this.state.templateHasSubmissionRules) {
            this.state.calculate();
          }
        }
      });
      this.availableTemplates = availableTemps;
      this._crd.detectChanges();
      this.runSubmissionRulesValidation();
      // if the default company template has not been chosen and more than one spreading template exists, then show the template selector popup
      if (this.availableTemplates.length > 1 && !this.state.documentFile.initialTemplateSelectionHasBeenMade) {
        this.showSpreadingTemplateSelectionPopup(true);
      }
    }));
  }

  showSpreadingTemplateSelectionPopup(initialTemplateSelection = false) {
    this._popupService.open({
      componentType: SelectSpreadingTemplateModalComponent,
      cssClass: 'modal-lg',
      inputs: {
        availableTemplates: this.availableTemplates,
        defaultTemplateId: this.state.spreadingTemplateId,
        initialTemplateSelection: initialTemplateSelection,
      },
      outputs: {
        selectedTemplateId: (selectedTemplateId: string) => {
          if (selectedTemplateId !== '') {
            this.chooseTemplate(selectedTemplateId);
          } else if (initialTemplateSelection) {
            this.redoManualReview();
          }
        }
      },
    });
  }

  loadRelatedStatements(statement) {
    const fileId = statement.documentFileId;
    return this._statementDataService.loadStatementsForFile(fileId, this.dataCache).then((statements: Array<Statement>) => {
      this.relatedStatements = statements;
      const relatedDASStatement = this.relatedStatements.filter(s => s.statementType === DYNAMIC_ADJUSTMENT_STATEMENT);
      if (relatedDASStatement.length) {
        this.spreadContainsDASStatement = true
        this.loadDASSnapshotData(statement.uuid, fileId)
      }
    });
  }


  loadDASSnapshotData(excludedStatementUuid, documentFileId){
    // TODO: revisit if excludedStatementUuid can be removed so that we can load all DAS snapshots
    // If can remove, we can also remove the buildDasSnapshots in validate-submission-rule.service.ts
    this._statementService.loadDASStatementSnapshot(excludedStatementUuid, documentFileId).subscribe(result => {
      if (result) {
        if (this.state) {
          this.state.dasRefSnapshot = result;
          this.state.calculate();
          this._crd.detectChanges();
        } else {
          this.dasRefSnapshot = result
        }

      }
    }, error => {
      console.error('Failed to load das statement snapshot for calculation reference')
    })
  }

  setEnableAccessToDoc() {
    this.enableAccessToDoc = this.state.documentFile.fileType.indexOf('PDF_') === 0;
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (this.loading) {
      return;
    }
    this.cellEditing({
      cellIdx: -1,
      row: null,
    });

    this.stopEditingText();
    this.stopInProgressCategorizations()
  }

  @HostListener('document:keydown', ['$event'])
  handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        if (document.activeElement.tagName === 'SELECT') {
          (document.activeElement as HTMLElement).blur();
        }
      }
    }
  }

  handleCategorizationDropdownEscape(event: KeyboardEvent, row: Row) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      row.showMappingBox = false;
      row.mappingBoxValidation = false;
      this._crd.markForCheck();
      return false;
    }
    return true;
  }

  stopInProgressCategorizations() {
    this.state.sourceRows.forEach(row => {
      row.showMappingBox = false;
    });
    this._crd.detectChanges();
  }

  stopPropagation(event){
    event.preventDefault();
    event.stopPropagation();
  }

  columnHeaderChanged(newValue: string, column: Column, attributeName: string) {
    this.madeAChange();
    column[attributeName] = newValue;
    this.stopEditingText();
    this.storeNewState(SPREAD_ACTION_COLUMN_METADATA_CHANGED);
    this.refreshTabStatusAfterEdit();
  }

  editColumnHeader(colIdx: number, attributeName: string) {
    this.stopEditingText();
    this.editingColumnHeaderIndex = colIdx;
    this.editingColumnHeader = attributeName;
    this._crd.detectChanges();
  }

  stopEditingText() {
    this.editingRowLabelIndex = -1;
    this.editingColumnHeaderIndex = -1;
    this.hideContextMenu();
  }

  loadOriginalDocumentDataForDownload() {
    this._documentFileService.getDownloadUrlForOriginalDocument(this.state.documentFile.uuid).subscribe(data => {
      this.originalDocumentFileUrl = data.originalFileUrl;
    });
  }

  downloadFile() {
    const url = this.originalDocumentFileUrl;
    const link = document.createElement('a');
    link.href = url;
    link.download = this.state.documentFile.originalDocumentName;
    link.target = '_blank';
    link.dispatchEvent(new MouseEvent('click'));
  }

  switchToStatement(statement: Statement) {
    this.stopEditingText();
    // If user edits a row label and clicks
    // directly on statement switch handle the save change
    this._crd.detectChanges();
    this.syncCurrentStatementTabStatus();

    const redirectCommands = ['statements', statement.uuid, 'edit'];
    const redirectExtras = { state: { shouldHideTemplate: this.shouldHideTemplate, documentSidebarIsOpen: this.documentSidebarIsOpen } };
    if (this.madeChanges) {
      this._popupService.open({
        componentType: ConfirmationPopupComponent,
        cssClass: 'modal-confirmation',
        inputs: {
          question: 'WARNING',
          text: 'You have unsaved changes. Save and continue?',
        },
        outputs: {
          callback: (approved: boolean) => {
            if (approved) {
              this.saveAndStay(redirectCommands, redirectExtras);
            }
          }
        },
      });
    } else if (this.isDASStatement) {
      this._popupService.open({
        componentType: ConfirmationPopupComponent,
        cssClass: 'modal-confirmation',
        inputs: {
          question: 'WARNING',
          text: 'Please save to ensure the calculated values in this view are available in other statements.',
          cancelButtonText: '',
        },
        outputs: {
          callback: (approved: boolean) => {
            if (approved) {
              this.saveAndStay(redirectCommands, redirectExtras);
            }
          }
        },
      });
    }
    else {
      this._router.navigate(redirectCommands, redirectExtras);
    }
  }

  ngAfterViewInit() {
    this._crd.detach();

    setTimeout(this.enableTooltips, 4000);
  }

  enableTooltips() {
    // Bootstrap tooltip initialization
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.forEach(tooltipTriggerEl => {
      return new bootstrap.Tooltip(tooltipTriggerEl)
    })
  }

  ngOnDestroy() {
    this._sharedDataService.showDefaultNavigation();

    this.userGuideService.remove(USER_GUIDES.CATEGORIZING_DATA);
    this.userGuideService.remove(USER_GUIDES.REVIEW_MAPPINGS_GUIDE);
    this.userGuideService.remove(USER_GUIDES.CORRECTING_DATA);
    this.userGuideService.remove(USER_GUIDES.SPREAD_ASSIST);
    this.userGuideService.remove(USER_GUIDES.MERGING_DATA);
    this.userGuideService.remove(USER_GUIDES.MAKING_ADJUSTMENTS);
    this.userGuideService.remove(USER_GUIDES.COMMENTING);
    this.userGuideService.remove(USER_GUIDES.TROUBLESHOOTING_SPREAD);
    this.userGuideService.remove(USER_GUIDES.SUPPORTED_TAX_RETURNS);
    this.userGuideService.remove(USER_GUIDES.REMOVING_DATA);

    this.hotkeys.forEach(hk => {
      this._hotkeysService.remove(hk);
    });

    this._spreadingHistoryService.clear(); // so when you leave, we don't keep the history.
    
    // Clear submission rule validation results related
    this.storedSubmissionRulesValidationResults = null;
  }

  getLabelForAdjustment(row: Row): string {
    return row.label + ' (Adj.)';
  }

  navigateToBorrower(companyUuid: string) {
    this._router.navigate(['/companies/' + companyUuid, 'financials', 'analysis']);
  }

  addAdjustmentRow(rowIdx: number, row: Row) {
    this.madeAChange();
    this.state.addAdjustmentForRow(rowIdx, this.getLabelForAdjustment(row));
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_ADJUSTMENT_CREATED);
  }

  showError(err: HttpErrorResponse) {
    alert(err);
  }

  setFirstBoxFocus(): void {
    const elements = document.getElementsByClassName('source-row-td');

    if (elements.length > 0) {
      // @ts-ignore
      elements[0].focus();
    }
  }

  doneLoading() {
    this.setFirstBoxFocus();
    this.hotkeys.forEach(hk => {
      this._hotkeysService.add(hk);
    });
    this.loading = false;
    this._crd.detectChanges();
  }

  _focusOnAutocomplete() {
    const itemNameInputClassName = 'autocomplete';
    setTimeout(() => { // shortterm fix to make sure item is there before we find
      const elements = document.getElementsByClassName(itemNameInputClassName);
      if (elements.length > 0) {
        const formulaNameInputBox = elements[0];
        // @ts-ignore
        formulaNameInputBox.focus();
        formulaNameInputBox.addEventListener('keyup', (evt: KeyboardEvent) => {
            evt.preventDefault();
            let delay = 250;
            // If it's keys for navigating the selection we want a faster response than if we're filtering the values.
            if ([27, 38, 40, 13, 9].includes(evt.keyCode)) {
                delay = 10;
            }
            this.delay(() => {
              if (elements.length < 1) {
                return true;
              }
              const domRect = elements[0].getBoundingClientRect();
              const spaceBelow = window.innerHeight - domRect.bottom;
              if (spaceBelow < 200) {
                setTimeout(() => {
                  const autocompleteElements = document.getElementsByClassName('item');
                  if (autocompleteElements.length > 2) {
                    const thirdAutoCompleteElement = autocompleteElements[2];
                    thirdAutoCompleteElement.scrollIntoView({behavior: 'smooth', block: 'end', inline: 'nearest'});
                  }
                }, 0);
              }
              this._crd.detectChanges();
            }, delay);
            return true;
        });

      }
    }, 50);
  }

  rightClickedRow(evt, rowIdx: number, row: Row) {
    this.stopEvent(evt);
    if (this.isDASStatement){
      return
    }
    this.state.deselectAll();
    row.selected = true;
    this.contextMenuMode = 'row';
    this.contextMenuRow = row;
    this.contextMenuObjectIndex = rowIdx;
    this.showContextMenuAtEventLocation(evt);
  }

  rightClickedColumn(evt, columnIdx: number, column: Column) {
    this.stopEvent(evt);

    // If the column is selected, leave it as is. If not, first select only that column
    if (!column.selected) {
      this.columnToggled(columnIdx, {
        'augment': false,
        'shift': false,
        'ctrl': false,
      });
    }
    this.contextMenuMode = 'column';
    this.contextMenuColumn = column;
    this.contextMenuObjectIndex = columnIdx;
    this.showContextMenuAtEventLocation(evt);
  }

  rightClickedCell(evt, row: Row, cell: Cell, cellIdx: number, rowIdx: number, showCommentClick=false) {
    this.stopEvent(evt);
    // do we need to handle ManualInputItem differently?
    // like if (this.isDASStatement && row?.itemClass !== "ManualInputItem") ??
    if (this.isDASStatement) {
      this.contextMenuMode = 'dasCell';
    } else if (showCommentClick) {
      this.contextMenuMode = 'sourceCellViewComment';
    } else {
      this.contextMenuMode = 'sourceCell';
    }
    this.contextMenuRow = row;
    this.contextMenuCell = cell;
    this.contextMenuObjectIndex = cellIdx;
    this.showContextMenuAtEventLocation(evt);
    this.cellSelected({
      'row': row,
      'cellIdx': cellIdx,
      'rowIdx': rowIdx,
    })
  }

  rightClickedStatementDate(evt, columnIdx: number, column: Column) {
    this.stopEvent(evt);
    this.contextMenuMode = "statementDate";
    this.contextMenuColumn = column;
    this.contextMenuObjectIndex = columnIdx;
    this.showContextMenuAtEventLocation(evt);
  }

  hasComment(column: Column): boolean {
    return column.sourceComments && column.sourceComments[0]?.statementId == this.statementId;
  }

  selectAndCategorize(evt, row: Row, rowIdx: number, validation = false) {
    if (row.itemClass==='supplementalItem') {
      return;
    }
    this.stopEvent(evt);
    if (this.isCancelActionOfInProgressCategorization(row, rowIdx, validation)) {
      row.showMappingBox = false;
      row.selected = false;
      this._crd.detectChanges();
      return
    }
    this.cancelAllInProgressCategorizationsAndDeselectAll();
    this.stopEditingText();
    row.selected = true;

    if (row.categorizedTo) {
      // This will now be "uncategorize"
      this.uncategorizeSelectedRows();
    } else {
      this.categorizeSelectedRows(validation);
    }
  }

  cancelAllInProgressCategorizationsAndDeselectAll() {
    this.state.sourceRows.forEach(row => {
      row.showMappingBox = false;
      row.selected = false;
    })
  }

  isCancelActionOfInProgressCategorization(row: Row, rowIdx: number, validation: boolean){
    // Clicking the 'c' or 'v' icon for a given row twice will cancel the in progress categorization and deselect
    if (row.categorizedTo || !row.showMappingBox){
      return false
    }
    return row.mappingBoxValidation == validation
  }


  showContextMenuAtEventLocation(evt) {
    const headerOffsetFromBottom = this.isDASStatement ? 150 : 300;
    const nonHeaderOffsetFromBottom = this.isDASStatement ? 150 : 400;
    const distFromBottom = evt.view.innerHeight - evt.pageY;
    let top = evt.pageY;

    // if header row cell, smaller context menu so less offset needed if too low
    if (this.contextMenuMode === 'row' && this.contextMenuRow.header && (distFromBottom < headerOffsetFromBottom)) {
      top = evt.pageY - (headerOffsetFromBottom - distFromBottom);
    }

    // if non header row cell, a larger context menu so less offset needed if too low
    if (this.contextMenuMode === 'row' && !this.contextMenuRow.header && (distFromBottom < nonHeaderOffsetFromBottom)) {
      top = evt.pageY - (nonHeaderOffsetFromBottom - distFromBottom);
    }

    this.contextMenuStyle = {
      'display': 'block',
      'top': `${top}px`,
      'left': `${evt.pageX}px`
    };

    this._crd.detectChanges();
  }

  hideContextMenu() {
    this.contextMenuStyle = {
      'display': 'none',
      'top': '0px',
      'left': '0px',
    };
    this.contextMenuMode = '';
    this.contextMenuRow = null;
    this.contextMenuColumn = null;
    this.contextMenuCell = null;
  }

  rowMapped(evt, row: Row) {
    this.itemMapped({
      'sourceRow': row,
      'templateLineItemId': evt.id,
      'isValidation': row.mappingBoxValidation ? true : false,
      'templateLineItemLabel': evt.value,
    });
  }

  itemMapped(c: Categorization) {
    this.madeAChange();
    this.state.categorizeAllSelectedRows(c);
    this.resetMappingBoxConfig();
    this.setScrollPosition();
    this.recalculateSelectedCellSum(false);
    this._crd.detectChanges();
    this.storeNewState(SPREAD_ACTION_CATEGORIZE);
  }

  selectRow(evt, rowIdx: number, row: Row) {
    this.rowToggled(rowIdx, {
      'augment': evt.altKey,
      'shift': evt.shiftKey,
      'ctrl': this.controlKey(evt),
      'row': row,
    });
  }

  rowToggled(idx: number, info: ItemClicked) {
    this.stopEditingText();
    if (info.shift) {
      this.state.rowSelectionAction(RowSelectionActionType.INCLUDE_UNTIL, idx)
    } else if (info.ctrl) {
      if (this.state.isRowSelected(idx)) { // want to unselect it
        this.state.rowSelectionAction(RowSelectionActionType.DESELECT_SINGLE, idx);
      } else { // if not selected, you want to select it
        this.state.rowSelectionAction(RowSelectionActionType.INCLUDE_ADDITIONAL, idx);
      }
    } else {
      this.state.rowSelectionAction(RowSelectionActionType.SELECT_SINGLE, idx);
    }
    this.recalculateSelectedCellSum(true, SPREAD_ACTION_TOGGLE_ROW);
    this._crd.detectChanges();
  }

  selectColumn(evt, colIdx: number, column: Column) {
    this.columnToggled(colIdx, {
      'augment': evt.altKey,
      'shift': evt.shiftKey,
      'ctrl': this.controlKey(evt),
    });
  }

  goToColumn(colIdx) {
    this.columnToggled(colIdx, {
      'augment': false,
      'shift': false,
      'ctrl': false,
    });
  }

  columnToggled(idx, info: ItemClicked) {
    this.stopEditingText();
    if (info.shift) {
      this.state.columnSelectionAction(RowSelectionActionType.INCLUDE_UNTIL, idx)
    } else if (info.ctrl) {
      if (this.state.isColumnSelected(idx)) { // want to unselect it
        this.state.columnSelectionAction(RowSelectionActionType.DESELECT_SINGLE, idx);
      } else { // if not selected, you want to select it
        this.state.columnSelectionAction(RowSelectionActionType.INCLUDE_ADDITIONAL, idx);
      }
    } else {
      // Single click on column - clear selected rows to indicate column-focused operation
      this.state.rowSelectionAction(RowSelectionActionType.DESELECT_ALL);
      this.state.columnSelectionAction(RowSelectionActionType.SELECT_SINGLE, idx);
    }
    this.recalculateSelectedCellSum(true, SPREAD_ACTION_TOGGLE_COLUMN);
    this._crd.detectChanges();
  }

  clearedCategorization(row: Row) {
    this.state.clearCategorization(row);
    this.storeNewState(SPREAD_ACTION_CLEAR_CATEGORIZATION);
    this._crd.detectChanges();
  }

  editCell(row: Row, cellIdx: number) {
    this.cellEditing({
      'row': row,
      'cellIdx': cellIdx,
    });
  }

  editCellAndStopEvent(evt, row: Row, cellIdx: number) {
    this.receivedValue = this.contextMenuCell.rawText
    this.stopEvent(evt);
    this.editCell(row, cellIdx);
  }

  cellEditing(cellEvt: CellEvent) {
    this.stopEditingText();
    this.state.sourceRows.forEach(r => {
      r.cellEditingIndex = -1;
      r.cellSelectedIndex = -1;
    });

    if (cellEvt.row) {
      cellEvt.row.cellSelectedIndex = cellEvt.cellIdx;
      cellEvt.row.cellEditingIndex = cellEvt.cellIdx;
    }

    this.state.calculate();
    this._crd.detectChanges();
  }

  highlightCell(row: Row, cellIdx: number, rowIdx: number) {
    this.stopEditingText();
    this.stopInProgressCategorizations();
    this.cellSelected({
      'row': row,
      'cellIdx': cellIdx,
      'rowIdx': rowIdx,
    });
  }

  clickLineItemLabel(row: Row) {
    this.resetDocumentViewSourceBox();
    
    if (!this.documentSidebarIsOpen || !row?.lineItemLabelSourceBox) {
      return
    }
    
    if (this.sourceBoxIsValid(row.lineItemLabelSourceBox, row.lineItemLabelSourceBox?.pageNumber)) {
      this.viewSourceBox = row.lineItemLabelSourceBox;
      this.viewSourcePage = row.lineItemLabelSourceBox.pageNumber;
      this._crd.detectChanges();
    } else if (row.lineItemLabelSourceBox?.pageNumber > -1) {
      this.viewSourceBox = null;
      this.viewSourcePage = row.lineItemLabelSourceBox.pageNumber;
      this._alertService.warning('The source cell could not be identified but the source page has been found.', 'Warning', 5);
      this._crd.detectChanges();
    }
    
  }

 handleKeyDown(cell, event: KeyboardEvent) {
  if (event.key === 'Escape') {
    this.justPressedEscape = true

    cell.value = this.receivedValue
    cell.rawText = this.receivedValue

    this.cellEditing({
      cellIdx: -1,
      row: null,
    });

    this.stopEditingText();
    this.state.rowSelectionAction(RowSelectionActionType.DESELECT_ALL);
    this.stopInProgressCategorizations();
    this.recalculateSelectedCellSum(false, 'deselect all');

    this.state.calculate();
    this._crd.detectChanges();
    return true;
  }
}

  cellSelected(cellEvt: CellEvent) {
    this.state.sourceRows.forEach(r => {
      r.cellEditingIndex = -1;
      r.cellSelectedIndex = -1;
    });

    cellEvt.row.cellSelectedIndex = cellEvt.cellIdx;

    // Select the row and column that cell is in
    this.state.rowSelectionAction(RowSelectionActionType.SELECT_SINGLE, cellEvt.rowIdx);
    this.state.columnSelectionAction(RowSelectionActionType.SELECT_SINGLE, cellEvt.cellIdx);

    if (this.documentSidebarIsOpen) {
      this.updateSourceCellBox(cellEvt.row, cellEvt.cellIdx);
    }
    this._crd.detectChanges();
  }

  doneEditingCell(newValue: string, row: Row, cellIdx: number) {
    if (this.justPressedEscape) {
      newValue = this.receivedValue
    }
    this.justPressedEscape = false;
    this.cellDoneEditing({
      'rawText': newValue,
      'row': row,
      'value': this.parseCellValueAsNumber(newValue),
      'cellIdx': cellIdx
    });
  }

  parseCellValueAsNumber(value: string): number {
    let noCommasOrWhitespace = value.replace(/[,\s]+/g, '');
    let multiplier = 1.0;
    if (noCommasOrWhitespace[0] === '(' && noCommasOrWhitespace[noCommasOrWhitespace.length - 1] === ')') {
      multiplier = -1.0;
      noCommasOrWhitespace = noCommasOrWhitespace.substring(1, noCommasOrWhitespace.length - 1);
    }

    if (this.isNumber(noCommasOrWhitespace)) {
      const parsed = parseFloat(noCommasOrWhitespace);
      return parsed * multiplier;
    }

    return NaN;
  }

  isNumber(number: string): boolean {
    const numberRegex = /^\s*[+-]?(\d+|\.\d+|\d+\.\d+|\d+\.)(e[+-]?\d+)?\s*$/;
    return numberRegex.test(number);
  }

  cellDoneEditing(cellEvt: CellEvent) {
    let didMakeAChange = false;

    if (cellEvt && cellEvt.rawText && (cellEvt.cellIdx || cellEvt.cellIdx === 0) &&
        cellEvt.row && cellEvt.row.cells &&
        cellEvt.rawText === cellEvt.row.cells[cellEvt.cellIdx].rawText) {
          // skip change if we know for sure that the edit
          // operation caused no change in value
          ;
    } else {
      this.madeAChange();
      didMakeAChange = true;
    }
    this.state.sourceRows.forEach(r => {
      r.cellEditingIndex = -1;
    });

    const cell = cellEvt.row.cells[cellEvt.cellIdx];
    // Value is a read-only prop. Raw text is the one we actually use.
    cell.value = this._roundingService.unRoundCellValue(
      this.state,
      cellEvt.row,
      cellEvt.value
    );

    // If it's a number, just set the raw text to that number so we "format and save it"
    if (!isNaN(cell.value)) {
      cell.rawText = cell.value.toString();
      cell.roundedRawText = this._roundingService
        .roundCellValue(this.state, cellEvt.row, cell.value)
        .toString();
    } else {
      cell.rawText = cellEvt.rawText;
      cell.roundedRawText = cellEvt.rawText;
    }

    if (isNaN(cell.value)) {
      cell.confidence = 0.0;
    } else {
      cell.confidence = 1.0;
    }

    this.state.calculate();

    if (didMakeAChange) {
      this.recalculateSelectedCellSum(true, SPREAD_ACTION_EDIT_A_CELL);
    }

    this.refreshTabStatusAfterEdit();
    this.saveChangesForCell(cell, cellEvt.row.lineItemId, cellEvt.value);
  }

  recalculateSelectedCellSum(shouldStoreState = true, action = 'no action specified') {
    const currentSum = this.state.getSelectedRowSum() as any;
    if (isNaN(parseFloat(currentSum))) {
      this.currentSum = 0.0;
    } else {
      this.currentSum = currentSum;
    }
    if (shouldStoreState) {
      this.storeNewState(action);
    }
  }

  validationSourceRowIsValid(row: Row) {
    const templateRow = this.state.templateMap.get(row.categorizedTo);
    return templateRow.cells.every(c => !c.offBy);
  }

  // CITI-2191: mirrors the pattern="[0-9]+(\.[0-9]+)?" validator on the DAS weight-percentage input
  // (spreading.component.html #weightAdjustmentCell). A non-empty weight must be a non-negative decimal;
  // Save/Complete must block exactly when that inline validator would show its error.
  private static readonly VALID_WEIGHT_PERCENTAGE_PATTERN = /^[0-9]+(\.[0-9]+)?$/;

  private hasInvalidDasWeightPercentage(): boolean {
    if (!this.isDASStatement) {
      return false;
    }
    return (this.state?.sourceRows ?? []).some(
      row => row.itemClass === 'AdjustedStandardItem' && this.isWeightPercentageInvalid(row.weightPercentage)
    );
  }

  private isWeightPercentageInvalid(weightPercentage: number | string): boolean {
    if (weightPercentage === null || weightPercentage === undefined || weightPercentage === '') {
      return false;
    }
    // No trimming: the inline pattern validator does not trim, so " 50" / whitespace must block here too.
    return !SpreadingComponent.VALID_WEIGHT_PERCENTAGE_PATTERN.test(String(weightPercentage));
  }

  private notifyInvalidDasWeightPercentage(
    message = 'Please enter a valid percentage value before saving or completing the spread.'
  ): void {
    this._alertService.error(message, 'Invalid Percentage');
  }

  saveAndStay(redirect = null, redirectExtras: { state?: Record<string, any> } = {}): void {
    if (this.hasInvalidDasWeightPercentage()) {
      this.notifyInvalidDasWeightPercentage();
      return;
    }
    this.save({complete: false, redirect: redirect, redirectExtras: redirectExtras, initialTemplateSelectionHasBeenMade: false, reAutonorm: false, allowDeletingInsertedRows: true, refreshPage: false }, false);
  }

  saveStatementWithComment(commentMetadata: CommentSaveMetadata): void {
      if (this.hasInvalidDasWeightPercentage()) {
        this.notifyInvalidDasWeightPercentage(
          'Please fix the invalid percentage value before saving your comment.'
        );
        return;
      }
      this.save({complete: false, redirect: null, initialTemplateSelectionHasBeenMade: false, reAutonorm: false, allowDeletingInsertedRows: true, refreshPage: false}, false, commentMetadata);
  }
  
  saveAndShowValidationResults(): void {
      this.save({complete: false, redirect: null, initialTemplateSelectionHasBeenMade: false, reAutonorm: false, allowDeletingInsertedRows: true, refreshPage: false }, false, null, true);
  }

  handleCompleteClick(): void {
    if (this.hasInvalidDasWeightPercentage()) {
      this.notifyInvalidDasWeightPercentage();
      return;
    }
    this.clickout(null);
    this._crd.detectChanges();
    if (this.madeChanges && this.state.templateHasSubmissionRules) {
      this.saveAndShowValidationResults();
      return
    }
    if (this.storedSubmissionRulesValidationResults === null) {
      // If validation is still in progress, show loading indicator when clicking on complete button
      this.loading = true;
      this._crd.detectChanges();
      const checkValidationResults = () => {
        if (this.storedSubmissionRulesValidationResults !== null) {
          // Validation is complete, reset loading and process validation results
          this.loading = false;
          this._crd.detectChanges();
          this.processValidationResults();
        } else {
          setTimeout(checkValidationResults, 100);
        }
      };
      checkValidationResults();
    } else {
      this.processValidationResults();
    }
  }

  private processValidationResults(): void {
    setTimeout(() => {
      // Timeout is needed to avoid complete event happens prior to calculate when user clicking on "Complete" button
      this.updateStoredValidationResultsWithCurrentStatement();
      const hasHardStops = this.storedSubmissionRulesValidationResults.some(result => result.status === 'hard-stop');
      const hasSoftStops = this.storedSubmissionRulesValidationResults.some(result => result.status === 'soft-stop');
      if (hasHardStops || hasSoftStops) {
        this.showSubmissionRulesValidationModal();
      } else {
        this.proceedWithComplete();
      }
    });
  }

  private proceedWithComplete(): void {
    if (this.attestationText) {
      this.openAttestationModal().then((accepted) => {
        this.userAcceptedAttestation = true;
        this.saveAndComplete();
      }, (rejected) => {
        return;
      })
    } else {
      this.saveAndComplete();
    }
  }

  saveAndComplete(): void {
    // Check to see if this statement has projections because this statement might not yet have been saved.
    const currentStatementHasProjections =
      this.state.statement.columns.filter(column => this.isProjectionOrProforma(column['scenario'])).length > 0;

    const companyId = this.state.documentFile.company;
    this.updateCurrencyForCompany();
    this._statementService.otherStatementHasProjections(
      companyId,
      this.state.documentFile.id,
      this.state.statement.id
    ).subscribe((anyStatementBesidesCurrentStatementHasProjections: boolean) => {
      if (currentStatementHasProjections || anyStatementBesidesCurrentStatementHasProjections) {
        this.performSaveWhenProjectionsArePresent();
      } else {
        this.performSaveWhenProjectionsAreNotPresent();
      }
    });
  }

  async openAttestationModal() {
    return new Promise((resolve: any, reject: any) => {
      this._popupService.open({
        componentType: AttestationPopupComponent,
        cssClass: 'modal-attestation',
        inputs: {
          title: 'ATTESTATION',
          attestationText: this.attestationText,
        },
        outputs: {
          callback: (approved: boolean) => {
            if (approved) {
              resolve();
            } else {
              reject();
            }
          }
        },
      });
    })
  }


  private syncCurrentStatementTabStatus(): void {
    this.validationSubmissionRuleService.syncCurrentStatementTabStatus(this.state);
  }


  private refreshTabStatusAfterEdit(options: { recalculate?: boolean } = {}): void {
    if (options.recalculate && this.state?.templateHasSubmissionRules) {
      this.state.calculate();
    }
    this.syncCurrentStatementTabStatus();
    this._crd.detectChanges();
  }

  /**
   * Runs submission rules validation and stores the results for later use
   * This method is called after the page is fully loaded and calculations are complete
   */
  private async runSubmissionRulesValidation(reloadDasSnapshots: boolean = false): Promise<void> {
    if (!this.state.templateHasSubmissionRules || !this.relatedStatements) {
      this.storedSubmissionRulesValidationResults = [];
      return;
    }
    try {
      this.storedSubmissionRulesValidationResults = await this.validateSubmissionRules(reloadDasSnapshots);
      this._crd.detectChanges();
      return
    } catch (error) {
      this.logger.error('Error running submission rules validation: ' + error.message, {'errorObject': error});
      this.storedSubmissionRulesValidationResults = [];
      return
    }
  }

  /**
   * Updates stored validation results with current statement's real-time submission rule results
   */
  private updateStoredValidationResultsWithCurrentStatement(): void {
    if (!this.state.templateHasSubmissionRules || !this.state.submissionRules) {
      return;
    }

    const currentStatementResults: ValidationResult[] = [];
    
    for (let colIdx = 0; colIdx < this.state.columns.length; colIdx++) {
      const column = this.state.columns[colIdx];
      const columnValidationResult: ValidationResult = {
        statementId: this.state.statement.id,
        statementType: this.state.statement.statementType,
        statementDate: column.statementDate,
        periodKeyUuid: column.periodKeyUuid,
        status: 'passing',
        rules: []
      };
      
      const rows = this.state.statementType === DYNAMIC_ADJUSTMENT_STATEMENT ? this.state.sourceRows : this.state.rows;
      this.processCurrentStatementRowsForValidation(rows, columnValidationResult, colIdx);
      currentStatementResults.push(columnValidationResult);
    }
    
    // Update stored validation results, replacing any existing results for current statement
    if (this.storedSubmissionRulesValidationResults) {
      // Remove any existing results for current statement
      this.storedSubmissionRulesValidationResults = this.storedSubmissionRulesValidationResults.filter(
        result => result.statementId !== this.state.statement.id
      );
    } else {
      this.storedSubmissionRulesValidationResults = [];
    }
    this.storedSubmissionRulesValidationResults.push(...currentStatementResults);
  }

  /**
   * Process current statement rows to collect submission rule validation results
   */
  private processCurrentStatementRowsForValidation(rows: any[], validationResult: ValidationResult, colIdx: number): void {
    for (const row of rows) {
      if (row.cells && row.cells[colIdx]) {
        const cell = row.cells[colIdx];
        if (cell?.submissionRuleResults?.length) {
          for (const ruleResult of cell.submissionRuleResults) {
            validationResult.rules.push({
              message: ruleResult.message,
              action: ruleResult.action,
              rowLabel: row.label,
              cellValue: cell.rawText || cell.value?.toString() || '',
              rowRef: row.ref
            });
            
            // Update validation status
            if (ruleResult.action === 'InsufficientHardStop') {
              validationResult.status = 'hard-stop';
            } else if (ruleResult.action === 'InsufficientSoftStop' && validationResult.status !== 'hard-stop') {
              validationResult.status = 'soft-stop';
            }
          }
        }
      }
      
      // Process child rows recursively
      if (row.children?.length) {
        this.processCurrentStatementRowsForValidation(row.children, validationResult, colIdx);
      }
    }
  }

  /**
   * Validates submission rules for all related statements
   */
  private validateSubmissionRules = async (reloadDasSnapshots: boolean = false): Promise<ValidationResult[]> => {
    // Prepare pre-loaded snapshots to avoid duplicate API calls
    const preLoadedSnapshots: { [cacheKey: string]: { stdFrameRefSnapshot?: Record<string, unknown> } } = {};
    
    // Add the current statement's stdFrameRefSnapshot if it exists
    if (this.state.statement.id && this.state.stdFrameRefSnapshot && Object.keys(this.state.stdFrameRefSnapshot).length > 0) {
      const cacheKey = this.validationSubmissionRuleService.generateCacheKey([this.state.statement]);
      preLoadedSnapshots[cacheKey] = {
        stdFrameRefSnapshot: this.state.stdFrameRefSnapshot
      };
    }
    
    return await this.validationSubmissionRuleService.validateAllStatementsWithFullState(
      this.relatedStatements, 
      this.state.submissionRules,
      this.dataCache,
      this.state,
      preLoadedSnapshots,
      reloadDasSnapshots
    );
  }

  private showSubmissionRulesValidationModal(): void {
    const availableStatements = this.relatedStatements || [];
    const roundingFactor = <ScaleType>(this.state?.reviewQueueItem?.multiplyByFactor || 'ACTUALS');
    this._popupService.open({
      componentType: SubmissionRulesValidationModalComponent,
      cssClass: 'modal-submission-rules-validation',
      inputs: {
        validationResults: this.storedSubmissionRulesValidationResults,
        availableStatements: availableStatements,
        roundingFactor: roundingFactor
      },
      outputs: {
        proceed: () => {
          this.proceedWithComplete();
        },
        navigateToValidationIssue: (navigationRequest: NavigationRequest) => {
          this.navigateToValidationIssue(navigationRequest);
        }
      }
    });
  }

  performSaveWhenProjectionsAreNotPresent() {
    this._borrowerService.getCompanyById(this.state.documentFile.company).subscribe((company: Company) => {
      this.save({
        complete: true,
        redirect: ['companies', company.uuid, 'financials', 'analysis'],
        initialTemplateSelectionHasBeenMade: false,
        reAutonorm: false,
        allowDeletingInsertedRows: true,
        refreshPage: false,
      });
    });
  }

  performSaveWhenProjectionsArePresent() {
    this._borrowerService.getCompanyById(this.state.documentFile.company).subscribe((company: Company) => {
      this.digitizationService
      .get(this.state.documentFile.id, this.state.reviewQueueItem.id)
      .subscribe((digitization: Digitization) => {
        if (!(!!digitization.projectionName) || digitization.projectionName.length === 0) {
          // Get the current projection column data for display
          const projectionColumn = this.state.columns.find(col => col.scenario === 'PROJECTION' || col.scenario === 'PROFORMA');
          const statementDate = projectionColumn?.statementDate || '';
          const reportingInterval = projectionColumn?.reportingInterval || '';

          // Prompt the user for the projection name since is not set and at least one scenario is a projection.
          this._popupService.open({
            componentType: PromptForProjectionNameComponent,
            cssClass: 'modal-confirmation',
            inputs: {
              statementDate: statementDate,
              reportingInterval: reportingInterval
            }
          }).then((popup: NgxPopupComponent) => {
            popup.addEventListener('close', (data: CustomEvent) => {
              if (data.detail === true) {
                this.save({
                  complete: true,
                  redirect: ['companies', company.uuid, 'financials', 'analysis'],
                  initialTemplateSelectionHasBeenMade: false,
                  reAutonorm: false,
                  allowDeletingInsertedRows: true,
                  refreshPage: false,
                });
              }
            }, {once: true});
          }).catch(error => {
            this._alertService.error(`${error.message}`);
          });
        } else {
          this.save({
            complete: true,
            redirect: ['companies', company.uuid, 'financials', 'analysis'],
            initialTemplateSelectionHasBeenMade: false,
            reAutonorm: false,
            allowDeletingInsertedRows: true,
            refreshPage: false,
          });
        }
      });
    });
  }

  openEditProjectionNameModal(column: any): void {
    // Prevent event bubbling
    event?.stopPropagation();

    // Get the current digitization to pass the ID to the modal
    this.digitizationService
      .get(this.state.documentFile.id, this.state.reviewQueueItem?.id)
      .subscribe((digitization: Digitization) => {
        // Open the projection name modal with the current column data and digitization ID
        this._popupService.open({
          componentType: PromptForProjectionNameComponent,
          cssClass: 'modal-confirmation',
          inputs: {
            statementDate: column.statementDate || '',
            reportingInterval: column.reportingInterval || '',
            digitizationId: digitization?.id || 0
          }
        }).then((popup: NgxPopupComponent) => {
          popup.addEventListener('close', (data: CustomEvent) => {
            if (data.detail === true) {
              // Refresh the data or update the UI as needed
              this._crd.detectChanges();
            }
          }, {once: true});
        }).catch(error => {
          this._alertService.error(`${error.message}`);
        });
      }, error => {
        this._alertService.error(`Failed to get digitization data: ${error.message}`);
      });
  }

  saveBeforeNavigatingAway(): Promise<any> {
    // temp save before merge footnotes starts so we don't lose data
    const isComplete = false;
    const shouldForceIntegrationSync = false;
    return this._statementDataService.save(this.state, isComplete, shouldForceIntegrationSync);
  }


  findSupplementalItem(statement: Statement) {
    return statement.rows && statement.rows.some(row => row.itemClass === 'supplementalItem');
  }


  save(options: { complete: boolean, redirect: any, redirectExtras?: { state?: Record<string, any> }, initialTemplateSelectionHasBeenMade: boolean, reAutonorm: boolean, allowDeletingInsertedRows: boolean, refreshPage: boolean } = {complete: false, redirect: null, initialTemplateSelectionHasBeenMade: false, reAutonorm: false, allowDeletingInsertedRows: false, refreshPage: false},
       reload: boolean = false,
       commentMetadata: CommentSaveMetadata = null,
       showValidationModal: boolean = false) {
    const complete = options.complete;
    const redirect = options.redirect;
    const redirectExtras = options.redirectExtras ?? {};
    const initialTemplateSelectionHasBeenMade = options.initialTemplateSelectionHasBeenMade;
    const reAutonorm = options.reAutonorm;
    const allowDeletingInsertedRows = options.allowDeletingInsertedRows;
    const refreshPage = options.refreshPage;

    this.state.spreadingTemplateId = this.selectedTemplate;

    this._popupService.open({
      componentType: SavingPopupComponent,
      cssClass: 'modal-confirmation'
    }).then(() => {
      return this._statementDataService.save(this.state, complete, this.forceIntegrationSync, initialTemplateSelectionHasBeenMade, reAutonorm, allowDeletingInsertedRows, this.userAcceptedAttestation, commentMetadata);
    }).then((statement) => {
      setTimeout(this.enableTooltips, 1000);

      if(this.findSupplementalItem(statement) && !this.findSupplementalItem(this.state.statement)){
        //synchronous method
        this.state.setStatement(statement);
        this._crd.detectChanges();
      }

      this._popupService.closeAll();
      this.madeChanges = false;
      if (!reAutonorm) {
        this._alertService.success('Spread saved!');
      } else {
        this._alertService.success('Successfully reset mappings!');
      }
      if (complete) {
        this.alreadyCompleted = true;
        this._crd.detectChanges();
      }

      try {
        this._trackingService.trackHumanInLoop({
          type: 'End',
          step: 'Spreading',
          documentFileId: this.getDocumentFileId(),
          documentCompanyId: this.state.documentFile.company,
          documentTenantId: this._userService.getBankId()
        });
      } catch (err) {
        this.logger.error('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
      }

      if (redirect) {
        if (complete) {
          const flow = this._sharedDataService.embeddedWorkflow$.getValue();
          if (flow) {
            ACCEPTABLE_URLS.forEach((acceptable_url) => {
              try {
                window.parent.postMessage({
                  'eventName': 'workflowComplete',
                  'workflow': {},
                }, acceptable_url);
              } catch (ex) {/* pass */}
            });

            if (!!flow.redirectUrl) {
              window.parent.location = flow.redirectUrl;
              return;
            } else {
              return;
            }
          }
        }

        this._router.navigate(redirect, redirectExtras);
        return;
      }

      if (reload) {
        this.loadData();
        this._popupService.closeAll();
      } else {
        // If no reload, need to run submission rules validation explicitly
        this.runSubmissionRulesValidation(true).then(() => {
          if (showValidationModal) {
            this.processValidationResults();
          }
          this._popupService.closeAll();
          this._crd.detectChanges();
        })
      }

      if (refreshPage) {
        window.location.reload();
      }
    }).catch(error => {
      console.error(error);
      this._popupService.closeAll();
      if (error && error?.message && String(error.message) !== 'undefined') {
          const title = error?.error_title ? error.error_title : UNKNOWN_ERROR_TOAST_TITLE;
          this._alertService.error(error.message, title)
      } else {
        this._alertService.error(UNKNOWN_ERROR_TOAST_BODY, UNKNOWN_ERROR_TOAST_TITLE);
      }
    });
  }

  cellShouldAlignRight(cell: Cell): boolean {
    return !isNaN(cell.value);
  }

  redoManualReview() {
    this._documentFileService.getReviewQueueItemForFileId(this.state.documentFile.id).subscribe((data) => {
      const reviewQueueItemId = data.response.objects[0].reviewQueueItem;
      this._router.navigate(['review', reviewQueueItemId, 'manual_review']);
    },
    error => {
        this._alertService.error('Unable to get Review Queue Item');
    });
  }

  toggleStepsToSuccess() {
    if (this._sharedDataService.shouldShowManualReviewSidebar === true) {
      this._sharedDataService.shouldShowManualReviewSidebar = false;
    } else {
      this._sharedDataService.shouldShowManualReviewSidebar = true;
    }
  }

  getAutocompleteOptionsForRow(row: Row): Array<AutocompleteOption> {
    if (row.derivedFromTemplate) {
      return this.findItemInTemplateMatchingLabel(row.label)
    } else if (row.mappingBoxValidation) {
      return this.state.autocompleteOptions.concat(this.state.rollupOptions);
    }
    return this.state.autocompleteOptions;
  }

  findItemInTemplateMatchingLabel(label) {
    let options: Array<AutocompleteOption> = [];
    for(const [, lineItem] of this.state.templateMap) {
      if (lineItem.label === label) {
        options.push({
          'id': lineItem.lineItemId,
          'value': lineItem.label,
        })
      }
    }
    return options
}

  rowLabelChanged(newValue: string, row: Row) {
    if (row.label !== newValue) {
      row.label = newValue;
      if (row.userFacingLabel) {
        row.userFacingLabel = newValue;
      }
      // Clear stale translation so toggle-on shows the newly edited label via fallback
      row.translatedLabel = null;
      // Remember this row was user-edited so grey/lock persists even without a translatedLabel
      row.labelEditedByUser = true;
      this.madeAChange();
      this.storeNewState(SPREAD_ACTION_ROW_LABEL_CHANGED);

      if (newValue && this.hasTranslations()) {
        const key = newValue.trim();
        const cached = this.translationDict.get(key);
        if (cached !== undefined) {
          row.translatedLabel = cached;
          this._crd.detectChanges();
        } else {
          this._translationService.translateLabel(key).subscribe({
            next: (translated) => {
              this.translationDict.set(key, translated);
              row.translatedLabel = translated;
              this._crd.detectChanges();
            },
            error: (err) => { this.logger.error('Failed to translate row label', err); }
          });
        }
      }
    }
    this.stopEditingText();
    this.refreshTabStatusAfterEdit();
  }


  handleEditRowLabel(rowIdx: number, rowItemClass?: string) {
    if (rowItemClass === "ManualInputItem") {
      this.warnBeforeAllowingManualInputLabelEdit(rowIdx);
    } else {
      this.beginEditingRowLabel(rowIdx);
    }
  }

  beginEditingRowLabel(rowIdx: number) {
    this.editingRowLabelIndex = rowIdx;
    this._crd.detectChanges();
  }

  warnBeforeAllowingManualInputLabelEdit(rowIdx: number) {
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: "Warning",
        text: 'Renaming this label will update its name across all other spreads for this entity. The change will take effect once the current spread is completed.',
        confirmButtonText: 'Continue'
      },
    }).then((popup: NgxPopupComponent) => {
      popup.addEventListener('close', (data: CustomEvent) => {
        if (data.detail === true) {
          this.beginEditingRowLabel(rowIdx);
        }
      }, {once: true});
    });
  }

  saveChangesForCell(cell: Cell, lineItemId: number, newValue: number) {
    this._statementDataService.changeCellValue(cell, lineItemId, newValue).then(() => {
      return;
    }).catch(err => {
      console.error(err);
    });
  }

  /**
   * Converts a column index to its excel equivalent in alphabet notation.
   *
   * E.g.
   *
   * 0 -> A
   * 1 -> B
   * ...
   * 25 -> Z
   * 26 -> AA
   * ...
   * etc
   *
   * @param idx
   */
  headerForColumnIndex(idx: number): string {
    let num = idx + 1;

    const lettersInAlphabet = 26;
    // Get us to the first letter after all the other stuff. This only works because the char codes
    // for letters are in alphabetic order and start at 65.
    const charCodeOffset = 65;
    let ret = '';
    for (let a = 1, b = lettersInAlphabet; (num -= a) >= 0; a = b, b *= lettersInAlphabet) {
      const charCode = ((num % b) / a) + charCodeOffset;
      ret = String.fromCharCode(charCode) + ret;
    }
    return ret;
  }

  sortStatements(statements: Array<Statement>, taxonomy: Array<TemplateItem>) {
    let sortOrder = {};
    taxonomy.forEach(templateItem => {
      if (templateItem.isDynamicAdjustmentStatement) {
        if (!sortOrder.hasOwnProperty(DYNAMIC_ADJUSTMENT_STATEMENT)) {
          sortOrder[DYNAMIC_ADJUSTMENT_STATEMENT] = {}
        }
        sortOrder[DYNAMIC_ADJUSTMENT_STATEMENT][templateItem.label] = taxonomy.indexOf(templateItem) + 1;
      }
      else if (templateItem['lineItemId'] in TAXONOMY_ID_STATEMENT_TYPE_LOOKUP) {
        sortOrder[TAXONOMY_ID_STATEMENT_TYPE_LOOKUP[templateItem['lineItemId']]] = taxonomy.indexOf(templateItem) + 1;
      }
    });

    return statements.sort((a: Statement, b: Statement): number => {
      if (!sortOrder.hasOwnProperty(a.statementType) || !sortOrder.hasOwnProperty(b.statementType)) {
        return 0;
      }

      const aOrder = a.statementType === DYNAMIC_ADJUSTMENT_STATEMENT ? sortOrder[a.statementType][a.uniqueDasName] : sortOrder[a.statementType];
      const bOrder =  b.statementType === DYNAMIC_ADJUSTMENT_STATEMENT ? sortOrder[b.statementType][b.uniqueDasName] : sortOrder[b.statementType];

      if (aOrder > bOrder) {
        return 1;
      } else if (aOrder < bOrder) {
        return -1;
      }
      return 0;
    });
  }

  willColumnOverwrite(column: any): boolean {

    if (this.isProjectionOrProforma(column.scenario)) {
      // We do not overwrite, delete, etc. projections.
      return false;
    }

    let doesColumnExist = false;
    this.existingColumns.forEach(element => {
      if (column.preparationType === element.preparationType &&
        column.fiscalYearEnd.includes(element.fiscalYearEnd) &&
        column.reportingPeriod === element.reportingPeriod
      ) {
        doesColumnExist = true;
      }
    });
    return doesColumnExist;
  }

  chooseTemplate(templateIdChosen: string): void {
    const numericTemplateId = parseInt(templateIdChosen, 10);
    this.selectedTemplate = numericTemplateId;

    if (numericTemplateId) {
      this.hasTemplate = true;
      this._crd.detectChanges();
      this.save({complete: false, redirect: null, initialTemplateSelectionHasBeenMade: true, reAutonorm: false, allowDeletingInsertedRows: false, refreshPage: true }, true);
    }
    return;
  }

  storeNewState(action = ''): void {
    const newStateAdded = this._spreadingHistoryService.addNewState(Object.assign({}, this.state), action);
    if (newStateAdded) {
      this.noMoreUndos = false;
    }
  }

  /**
   * Restores the state from the provided history data.
   * @param spreadingHistoryData - The history data containing the state to restore
   */
  private restoreState(spreadingHistoryData: SpreadingState): void {
    if (!spreadingHistoryData) {
      return;
    }

    const newStateValues = spreadingHistoryData['newStateValues'];
    this.noMoreUndos = spreadingHistoryData['noMoreUndos'];
    this.noMoreRedos = spreadingHistoryData['noMoreRedos'];
    this.state.rows = newStateValues.rows;
    this.state.sourceRows = newStateValues.sourceRows;
    this.state.statement.rows = newStateValues.sourceRows;
    this.state.referenceRows = newStateValues.referenceRows;
    this.state.columns = newStateValues.columns;
    this.state.statement.columns = newStateValues.columns;
    this.state.rebuildRowReferences();
    this._rehydrateTranslationsFromDict();
  }

  /**
   * Re-applies cached translations to restored rows after an undo/redo. History snapshots can be
   * taken before an async translation resolves, so a restored row may have lost its
   * translatedLabel; fill it from the dictionary so the translate-to-English view stays correct
   * without re-calling the API. Existing translations are left untouched.
   */
  private _rehydrateTranslationsFromDict(): void {
    if (!this.hasTranslations()) {
      return;
    }
    [...(this.state?.sourceRows ?? []), ...(this.state?.rows ?? [])].forEach(row => {
      if (!row?.label || row.translatedLabel) {
        return;
      }
      const cached = this.translationDict.get(row.label.trim());
      if (cached !== undefined && cached !== row.label) {
        row.translatedLabel = cached;
      }
    });
  }

  undo(): void {
    this.undoButtonLoading = true;
    setTimeout(() => {
      this._crd.detectChanges();

      const spreadingHistoryData = this._spreadingHistoryService.undo();
      this.restoreState(spreadingHistoryData);
      
      this.undoButtonLoading = false;
      this.state.calculate();
      this._crd.detectChanges();
    }, 300);
  }

  redo(): void {
    this.redoButtonLoading = true;
    setTimeout(() => {
      const spreadingHistoryData = this._spreadingHistoryService.redo();
      this.restoreState(spreadingHistoryData);
      
      this.redoButtonLoading = false;
      this.state.calculate();
      this._crd.detectChanges();
    }, 300);
  }

  hideTemplate() {
    document.getElementById('template').style.display = 'none';
    document.getElementById('template-spacer').style.display = 'none';
    this.shouldHideTemplate = true;
  }

  toggleTemplateView(): void {
    if (this.reSpreadInProgress) {
      return
    }
    const template = document.getElementById('template');
    const templateContainer = document.getElementById('template-spacer');

    if (!template || !templateContainer) {
      return
    }

    this.shouldHideTemplate = !this.shouldHideTemplate

    if (this.shouldHideTemplate) {
      template.style.display = 'none';
      templateContainer.style.display = 'none';
    } else {
      template.style.display = 'block';
      templateContainer.style.display = 'block';
    }
  }

  updateCurrencyForCompany() {
    this._borrowerService.setSetting('currency', this.state.documentFile.currency, this.companyObject);
  }

  reAutonormDocument() {
    if (this.reSpreadInProgress) {
      return
    }
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: 'RESET MAPPINGS',
        text: 'This will re-run the autonormalization algorithms and reset all mappings for this document, removing any manual adjustments you have made to the spread. Are you sure you want to reset the mappings for this document?',
        confirmButtonText: 'Yes'
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this.save({complete: false, redirect: null, initialTemplateSelectionHasBeenMade: false, reAutonorm: true, allowDeletingInsertedRows: false, refreshPage: false}, true);
          }
        }
      },
    });
  }

  private stopEvent(evt = null) {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }
  }

  /**
   * Turns off the flag for showing the mapping box and other related
   * attributes on all source rows
   */
  private resetMappingBoxConfig() {
    this.state.sourceRows.forEach(r => {
      r.showMappingBox = false;
      r.mappingBoxValidation = false;
    });
  }

  private controlKey(evt) {
    if (IS_MAC) {
      return evt.metaKey; // command/apple key
    } else { // assume windows
      return evt.ctrlKey; // use ctrl key
    }
  }

  private resetDocumentViewSourceBox() {
    this.viewSourceBox = null;
    this.viewSourcePage = null
  }


  toggleIncludeItem(row) {
    this.state.calculate();
    this.madeAChange();
    this._crd.detectChanges();
  }

  weightInputChange(row){
    try {
      row.weightDecimal = +((parseFloat(row.weightPercentage) / 100).toFixed(2));
      this.madeAChange();
    } catch (err){
      this.logger.error('Failed to convert weight percentage to decimal: ' + err?.message, {'errorObject': err});
    }
  }

  isCellEditingDisabled(row) {
    if (!this.isDASStatement){
      return false
    }
    return ['AdjustedStandardItem', 'NonAdjustableStandardItem'].includes(row?.itemClass) ||
      ['asReported', 'customCalculation'].includes(row?.dynamicAdjustmentStatementItemType)
  }

  /*
   * Get display rounding label from current selected rounding key
   */
  getDisplayRounding() {
    const roundingSelection = <ScaleType>(
      this.state?.reviewQueueItem?.multiplyByFactor
    );
    return ROUNDING_MAP[roundingSelection];
  }

  /*
   * Get rounded value from display rounding/multiplication
   */
  getRoundedValue(cellValue, row) {
    const roundedVal = this._roundingService.roundCellValue(this.state, row, cellValue);
    return roundedVal;
  }

  /*
   * Check if scenario is projection or proforma
   */
  isProjectionOrProforma(scenario: string): boolean {
    return scenario === SCENARIO_TYPE_PROJECTION || scenario === SCENARIO_TYPE_PROFORMA;
  }

  protected readonly DYNAMIC_ADJUSTMENT_STATEMENT = DYNAMIC_ADJUSTMENT_STATEMENT;

  private navigateToValidationIssue(navigationRequest: NavigationRequest): void {
    const { statementId, statementDate, periodKeyUuid, rowRef } = navigationRequest;
    
    // Find the target statement
    const targetStatement = this.relatedStatements?.find(s => s.id === statementId);
    if (!targetStatement) {
      console.warn('Target statement not found for navigation');
      return;
    }

    // If we need to switch to a different statement, store navigation info in localStorage
    if (targetStatement.id !== this.state.statement.id) {
      const navigationData = <NavigationRequest>{
        navigateTo: 'validation',
        rowRef: rowRef,
        statementDate: statementDate,
        periodKeyUuid: periodKeyUuid
      };
      localStorage.setItem('navigateToSubmissionRuleResult', JSON.stringify(navigationData));
      this.switchToStatement(targetStatement);
      return;
    }

    this.executeNavigation(navigationRequest);
  }

  private executeNavigation(navigationRequest: NavigationRequest): void {
    const { statementDate, periodKeyUuid, rowRef } = navigationRequest;
    
    let targetColumnIndex = -1;
    if (periodKeyUuid) {
      targetColumnIndex = this.state.columns.findIndex(col => col.periodKeyUuid === periodKeyUuid);
    }
    if (targetColumnIndex === -1) {
      targetColumnIndex = this.state.columns.findIndex(col => col.statementDate === statementDate);
    }
    if (targetColumnIndex >= 0 && targetColumnIndex !== this.state.getLastSelectedItemIndex('column')) {
      this.goToColumn(targetColumnIndex);
    }

    this.scrollToSpecificValidationIssueByRef(rowRef);
  }

  private scrollToSpecificValidationIssueByRef(rowRef: string): void {
    if (!rowRef) {
      console.warn('No rowRef provided for navigation');
      return;
    }
    
    // Scroll to target element using the data-bs-target attribute
    const targetRow = document.querySelector(`[data-bs-target="#${rowRef}"]`);
    if (targetRow) {
      targetRow.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    } else {
      console.warn(`Template row with ref ${rowRef} not found, falling back to general scrolling`);
    }
  }

  private navigateToSubmissionRuleResult(): void {
    const pendingNavigation = localStorage.getItem('navigateToSubmissionRuleResult');
    if (pendingNavigation) {
      try {
        const navigationData = JSON.parse(pendingNavigation);
        if (navigationData.navigateTo === 'validation') {
          setTimeout(() => {
            const navigationRequest = {
              statementDate: navigationData.statementDate,
              periodKeyUuid: navigationData.periodKeyUuid,
              rowLabel: navigationData.rowLabel,
              rowRef: navigationData.rowRef
            };
            this.executeNavigation(navigationRequest);
          });
        }
      } catch (error) {
        console.warn('Invalid navigation data in localStorage:', error);
      } finally {
        localStorage.removeItem('navigateToSubmissionRuleResult');
      }
    }
  }
}